// StockTV — Shared Components
// Exported to window.* for use in main app

const { useState, useEffect, useRef, useMemo, useCallback } = React;

// Dark-only mode: all palette slots point at the same dark values.
// Light and amoled variants were removed 2026-05-18 — too much whack-a-mole
// without visual-diff CI. _th(anything) now returns the dark palette so every
// existing _th(theme) call site continues to resolve without change.
const _DARK_TH={bg:'#06090f',surface:'rgba(12,18,30,0.98)',text:'#e8edf5',sub:'#8899aa',border:'rgba(255,255,255,0.07)',gridLine:'rgba(255,255,255,0.06)',overlay:'rgba(3,5,11,0.97)',overlayBorder:'rgba(255,255,255,0.09)'};
const _TH={dark:_DARK_TH,light:_DARK_TH,amoled:_DARK_TH};
function _th(theme){return _DARK_TH;}

// ── Data ───────────────────────────────────────────────────────────────────
const STOCKS = {
  AAPL: { name:'Apple Inc.', sector:'tech', seed:1 },
  TSLA: { name:'Tesla, Inc.', sector:'auto', seed:2 },
  MSFT: { name:'Microsoft Corp.', sector:'tech', seed:3 },
  NVDA: { name:'NVIDIA Corp.', sector:'tech', seed:4 },
  GOOGL: { name:'Alphabet Inc.', sector:'tech', seed:5 },
  AMZN: { name:'Amazon.com', sector:'consumer', seed:6 },
  META: { name:'Meta Platforms', sector:'tech', seed:7 },
  JNJ: { name:'Johnson & Johnson', sector:'health', seed:8 },
  PFE: { name:'Pfizer Inc.', sector:'health', seed:9 },
  UNH: { name:'UnitedHealth Group', sector:'health', seed:10 },
  XOM: { name:'Exxon Mobil', sector:'energy', seed:11 },
  CVX: { name:'Chevron Corp.', sector:'energy', seed:12 },
  COP: { name:'ConocoPhillips', sector:'energy', seed:19 },
  EOG: { name:'EOG Resources', sector:'energy', seed:100 },
  MPC: { name:'Marathon Petroleum', sector:'energy', seed:101 },
  JPM: { name:'JPMorgan Chase', sector:'finance', seed:13 },
  BAC: { name:'Bank of America', sector:'finance', seed:14 },
  GS: { name:'Goldman Sachs', sector:'finance', seed:18 },
  WFC: { name:'Wells Fargo', sector:'finance', seed:27 },
  C: { name:'Citigroup Inc.', sector:'finance', seed:102 },
  MS: { name:'Morgan Stanley', sector:'finance', seed:103 },
  GM: { name:'General Motors', sector:'auto', seed:15 },
  RIVN: { name:'Rivian Automotive', sector:'auto', seed:23 },
  F: { name:'Ford Motor Co.', sector:'auto', seed:24 },
  STLA: { name:'Stellantis N.V.', sector:'auto', seed:104 },
  LCID: { name:'Lucid Group', sector:'auto', seed:105 },
  WMT: { name:'Walmart Inc.', sector:'consumer', seed:16 },
  COST: { name:'Costco Wholesale', sector:'consumer', seed:17 },
  HD: { name:'Home Depot', sector:'consumer', seed:22 },
  TGT: { name:'Target Corp.', sector:'consumer', seed:106 },
  LOW: { name:'Lowe\'s Companies', sector:'consumer', seed:107 },
  ABBV: { name:'AbbVie Inc.', sector:'health', seed:20 },
  MRK: { name:'Merck & Co.', sector:'health', seed:21 },
  LLY: { name:'Eli Lilly', sector:'health', seed:25 },
  SLB: { name:'SLB (Schlumberger)', sector:'energy', seed:26 },
  NFLX: { name:'Netflix Inc.', sector:'tech', seed:28 },
  AMD: { name:'Advanced Micro Dev.', sector:'ai', seed:29 },
  ORCL: { name:'Oracle Corp.', sector:'tech', seed:130 },
  CRM: { name:'Salesforce Inc.', sector:'tech', seed:131 },
  TMO: { name:'Thermo Fisher Sci.', sector:'health', seed:132 },
  CVS: { name:'CVS Health Corp.', sector:'health', seed:133 },
  PSX: { name:'Phillips 66', sector:'energy', seed:134 },
  OXY: { name:'Occidental Petroleum', sector:'energy', seed:135 },
  BLK: { name:'BlackRock Inc.', sector:'finance', seed:136 },
  AXP: { name:'American Express', sector:'finance', seed:137 },
  HMC: { name:'Honda Motor Co.', sector:'auto', seed:138 },
  NKE: { name:'Nike Inc.', sector:'consumer', seed:139 },
  EBAY: { name:'eBay Inc.', sector:'consumer', seed:140 },
  O: { name:'Realty Income Corp.', sector:'realestate', seed:141 },
  AVB: { name:'AvalonBay Comm.', sector:'realestate', seed:142 },
  HLT: { name:'Hilton Worldwide', sector:'travel', seed:143 },
  CCL: { name:'Carnival Corp.', sector:'travel', seed:144 },
  HSY: { name:'Hershey Co.', sector:'food', seed:145 },
  GIS: { name:'General Mills', sector:'food', seed:146 },
  MMM: { name:'3M Company', sector:'industrial', seed:147 },
  LHX: { name:'L3Harris Tech.', sector:'industrial', seed:148 },
  BIIB: { name:'Biogen Inc.', sector:'biotech', seed:149 },
  AMGN: { name:'Amgen Inc.', sector:'biotech', seed:150 },
  HII: { name:'Huntington Ingalls', sector:'defense', seed:151 },
  TDG: { name:'TransDigm Group', sector:'defense', seed:152 },
  CMCSA: { name:'Comcast Corp.', sector:'telecom', seed:153 },
  LUMN: { name:'Lumen Technologies', sector:'telecom', seed:154 },
  ECL: { name:'Ecolab Inc.', sector:'materials', seed:155 },
  NUE: { name:'Nucor Corp.', sector:'materials', seed:156 },
  INTC: { name:'Intel Corp.', sector:'ai', seed:157 },
  QCOM: { name:'Qualcomm Inc.', sector:'ai', seed:158 },
  LTC: { name:'Litecoin', sector:'crypto', seed:206 },
  LINK: { name:'Chainlink', sector:'crypto', seed:207 },
  SQ: { name:'Block Inc.', sector:'cryptostocks', seed:159 },
  PYPL: { name:'PayPal Holdings', sector:'cryptostocks', seed:160 },
  FUBO: { name:'FuboTV Inc.', sector:'streaming', seed:161 },
  AMCX: { name:'AMC Networks', sector:'streaming', seed:162 },
  SONY: { name:'Sony Group Corp.', sector:'intl', seed:163 },
  BIDU: { name:'Baidu Inc.', sector:'intl', seed:164 },
  // Real Estate
  AMT: { name:'American Tower', sector:'realestate', seed:30 },
  PLD: { name:'Prologis Inc.', sector:'realestate', seed:31 },
  SPG: { name:'Simon Property Grp.', sector:'realestate', seed:32 },
  WELL: { name:'Welltower Inc.', sector:'realestate', seed:33 },
  EQR: { name:'Equity Residential', sector:'realestate', seed:34 },
  PSA: { name:'Public Storage', sector:'realestate', seed:108 },
  // Travel & Leisure
  DAL: { name:'Delta Air Lines', sector:'travel', seed:35 },
  UAL: { name:'United Airlines', sector:'travel', seed:36 },
  MAR: { name:'Marriott Intl.', sector:'travel', seed:37 },
  ABNB: { name:'Airbnb Inc.', sector:'travel', seed:38 },
  RCL: { name:'Royal Caribbean', sector:'travel', seed:39 },
  LUV: { name:'Southwest Airlines', sector:'travel', seed:109 },
  // Food & Beverage
  MCD: { name:'McDonald\'s', sector:'food', seed:40 },
  SBUX: { name:'Starbucks Corp.', sector:'food', seed:41 },
  KO: { name:'Coca-Cola Co.', sector:'food', seed:42 },
  PEP: { name:'PepsiCo Inc.', sector:'food', seed:43 },
  YUM: { name:'Yum! Brands', sector:'food', seed:44 },
  DPZ: { name:'Domino\'s Pizza', sector:'food', seed:120 },
  // Industrials
  CAT: { name:'Caterpillar Inc.', sector:'industrial', seed:45 },
  DE: { name:'Deere & Company', sector:'industrial', seed:46 },
  HON: { name:'Honeywell Intl.', sector:'industrial', seed:47 },
  GE: { name:'GE Aerospace', sector:'industrial', seed:48 },
  UPS: { name:'United Parcel Svc.', sector:'industrial', seed:49 },
  ITW: { name:'Illinois Tool Works', sector:'industrial', seed:110 },
  // Biotech
  MRNA: { name:'Moderna Inc.', sector:'biotech', seed:50 },
  BNTX: { name:'BioNTech SE', sector:'biotech', seed:51 },
  GILD: { name:'Gilead Sciences', sector:'biotech', seed:52 },
  REGN: { name:'Regeneron Pharma.', sector:'biotech', seed:53 },
  VRTX: { name:'Vertex Pharma.', sector:'biotech', seed:54 },
  BMY: { name:'Bristol-Myers Squibb', sector:'biotech', seed:111 },
  // Defense
  LMT: { name:'Lockheed Martin', sector:'defense', seed:55 },
  RTX: { name:'RTX Corporation', sector:'defense', seed:56 },
  NOC: { name:'Northrop Grumman', sector:'defense', seed:57 },
  GD: { name:'General Dynamics', sector:'defense', seed:58 },
  BA: { name:'Boeing Co.', sector:'defense', seed:59 },
  LDOS: { name:'Leidos Holdings', sector:'defense', seed:112 },
  // Telecom
  T: { name:'AT&T Inc.', sector:'telecom', seed:60 },
  VZ: { name:'Verizon Comm.', sector:'telecom', seed:61 },
  TMUS: { name:'T-Mobile US', sector:'telecom', seed:62 },
  CHTR: { name:'Charter Comm.', sector:'telecom', seed:63 },
  SIRI: { name:'Sirius XM', sector:'telecom', seed:113 },
  DISH: { name:'DISH Network', sector:'telecom', seed:114 },
  // Materials
  LIN: { name:'Linde plc', sector:'materials', seed:64 },
  APD: { name:'Air Products', sector:'materials', seed:65 },
  NEM: { name:'Newmont Corp.', sector:'materials', seed:66 },
  FCX: { name:'Freeport-McMoRan', sector:'materials', seed:67 },
  DOW: { name:'Dow Inc.', sector:'materials', seed:115 },
  DD: { name:'DuPont de Nemours', sector:'materials', seed:116 },
  // AI / Semiconductors
  AVGO: { name:'Broadcom Inc.', sector:'ai', seed:68 },
  TSM: { name:'Taiwan Semi (TSMC)', sector:'ai', seed:69 },
  MU: { name:'Micron Technology', sector:'ai', seed:70 },
  SMCI: { name:'Super Micro Computer', sector:'ai', seed:71 },
  // Crypto stocks (exchanges, miners, custodians)
  COIN: { name:'Coinbase Global', sector:'cryptostocks', seed:72 },
  MSTR: { name:'MicroStrategy', sector:'cryptostocks', seed:73 },
  MARA: { name:'Marathon Digital', sector:'cryptostocks', seed:74 },
  RIOT: { name:'Riot Platforms', sector:'cryptostocks', seed:75 },
  HOOD: { name:'Robinhood Markets', sector:'cryptostocks', seed:76 },
  CLSK: { name:'CleanSpark Inc.', sector:'cryptostocks', seed:117 },
  // Top cryptocurrencies — TwelveData symbols are e.g. BTC/USD, but the
  // receiver UI uses bare tickers everywhere; the backend market provider
  // appends /USD when proxying to TD. Seeds are realistic 2026-Q2 prices.
  BTC: { name:'Bitcoin', sector:'crypto', seed:200 },
  ETH: { name:'Ethereum', sector:'crypto', seed:201 },
  SOL: { name:'Solana', sector:'crypto', seed:202 },
  XRP: { name:'XRP', sector:'crypto', seed:203 },
  ADA: { name:'Cardano', sector:'crypto', seed:204 },
  DOGE: { name:'Dogecoin', sector:'crypto', seed:205 },
  // Streaming / Media
  DIS: { name:'Walt Disney Co.', sector:'streaming', seed:77 },
  WBD: { name:'Warner Bros Discovery', sector:'streaming', seed:78 },
  SPOT: { name:'Spotify Technology', sector:'streaming', seed:79 },
  ROKU: { name:'Roku Inc.', sector:'streaming', seed:80 },
  PARA: { name:'Paramount Global', sector:'streaming', seed:118 },
  // International
  BABA: { name:'Alibaba Group', sector:'intl', seed:81 },
  TM: { name:'Toyota Motor Corp.', sector:'intl', seed:82 },
  NVS: { name:'Novartis AG', sector:'intl', seed:83 },
  SHOP: { name:'Shopify Inc.', sector:'intl', seed:84 },
  ASML: { name:'ASML Holding', sector:'intl', seed:85 },
  SAP: { name:'SAP SE', sector:'intl', seed:119 },
  // Recent IPOs — populated by /api/market/ipos. Hardcoded placeholder
  // values stay blurred (.stale-price-data class) until live data lands;
  // once applyQuote runs, blur clears and real prices show. These entries
  // exist so the SectorTile filter `STOCKS[t]` doesn't drop the IPO list.
  ARM: { name:'Arm Holdings', sector:'ipos', seed:86 },
  RDDT: { name:'Reddit, Inc.', sector:'ipos', seed:87 },
  CART: { name:'Maplebear (Instacart)', sector:'ipos', seed:88 },
  BIRK: { name:'Birkenstock Holding', sector:'ipos', seed:89 },
  ASTS: { name:'AST SpaceMobile', sector:'ipos', seed:90 },
  KVYO: { name:'Klaviyo', sector:'ipos', seed:91 },
  VLT: { name:'Veralto', sector:'ipos', seed:92 },
  TEM: { name:'Tempus AI', sector:'ipos', seed:93 },
  LB: { name:'LandBridge', sector:'ipos', seed:94 },
  SMR: { name:'NuScale Power', sector:'ipos', seed:95 },
  // Commodity futures — prices pushed via SSE applyQuote into both COMMODITIES
  // (legacy) and STOCKS (so SectorCluster can render them like any equity).
  // Realistic 2026-Q2 placeholders so the cells render non-blank before SSE
  // arrives; the .stale-price-data class still blurs them until applyQuote
  // marks them live, so users don't see "real" data on stale values.
  'GC=F': { name:'Gold',    sector:'commodities', seed:300, price:2350, changePct:0 },
  'SI=F': { name:'Silver',  sector:'commodities', seed:301, price:28.5, changePct:0 },
  'CL=F': { name:'WTI Oil', sector:'commodities', seed:302, price:78.2, changePct:0 },
  'NG=F': { name:'Nat Gas', sector:'commodities', seed:303, price:2.6,  changePct:0 },
  'HG=F': { name:'Copper',  sector:'commodities', seed:304, price:4.5,  changePct:0 },
  'BZ=F': { name:'Brent',   sector:'commodities', seed:305, price:82.1, changePct:0 },
};

// Seed-derived placeholder prices for every STOCKS entry without one. The
// SectorCluster filter (`typeof STOCKS[t].price==='number'`) used to drop
// every entry until SSE landed, which is why some sectors (commodities,
// crypto, certain rows) appeared empty on fresh launch. We now hydrate a
// deterministic placeholder per ticker from the seed; the .stale-price-data
// blur class keeps the value visually marked as "loading" until applyQuote
// flips _isLive(sym) → true. Skip entries that already shipped a price
// (commodities above, anything future-added with an explicit value).
(function _hydrateSeedPrices(){
  if(typeof window!=='undefined'&&!window.__liveSymbols)window.__liveSymbols=new Set();
  const keys = Object.keys(STOCKS);
  for (let i=0;i<keys.length;i++){
    const sym=keys[i];
    const s = STOCKS[sym];
    if (!s) continue;
    if (typeof s.price !== 'number') {
      // Deterministic 1–500 USD band derived from the seed so prices feel
      // reasonable for typical equities. Real SSE quotes overwrite this.
      const seed = (typeof s.seed === 'number') ? s.seed : 1;
      const r = ((seed * 2654435761) >>> 0) / 0xffffffff;
      s.price = +(5 + r * 495).toFixed(2);
    }
    if (typeof s.changePct !== 'number') s.changePct = 0;
    // Do not mark seed-derived placeholders live. applyQuote() owns
    // __liveSymbols so placeholder prices stay visually stale until actual
    // market data arrives.
  }
})();

const ALL_SECTORS = [
  { id:'tech',        name:'Technology',    color:'#3b82f6', desc:'Software, hardware & the internet',    tickers:['AAPL','MSFT','NVDA','GOOGL','META','NFLX','ORCL','CRM'] },
  { id:'health',      name:'Healthcare',    color:'#16a34a', desc:'Pharma, biotech & insurance',          tickers:['JNJ','PFE','UNH','ABBV','MRK','LLY','TMO','CVS'] },
  { id:'energy',      name:'Energy',        color:'#f59e0b', desc:'Oil, gas & renewables',               tickers:['XOM','CVX','COP','SLB','EOG','MPC','PSX','OXY'] },
  { id:'finance',     name:'Finance',       color:'#8b5cf6', desc:'Banks, insurance & fintech',          tickers:['JPM','BAC','GS','WFC','C','MS','BLK','AXP'] },
  { id:'auto',        name:'Automotive',    color:'#ef4444', desc:'Cars, EVs & mobility',                tickers:['TSLA','GM','RIVN','F','STLA','LCID','TM','HMC'] },
  { id:'consumer',    name:'Consumer',      color:'#06b6d4', desc:'Retail, e-commerce & brands',         tickers:['AMZN','WMT','COST','HD','TGT','LOW','NKE','EBAY'] },
  { id:'realestate',  name:'Real Estate',   color:'#84cc16', desc:'REITs, commercial & residential',     tickers:['AMT','PLD','SPG','WELL','EQR','PSA','O','AVB'] },
  { id:'travel',      name:'Travel',        color:'#f97316', desc:'Airlines, hotels & leisure',          tickers:['DAL','UAL','MAR','ABNB','RCL','LUV','HLT','CCL'] },
  { id:'food',        name:'Food & Bev.',   color:'#ec4899', desc:'Restaurants, drinks & brands',        tickers:['MCD','SBUX','KO','PEP','YUM','DPZ','HSY','GIS'] },
  { id:'industrial',  name:'Industrials',   color:'#94a3b8', desc:'Manufacturing, aerospace & logistics', tickers:['CAT','DE','HON','GE','UPS','ITW','MMM','LHX'] },
  { id:'biotech',     name:'Biotech',       color:'#a78bfa', desc:'Drug development & genomics',         tickers:['MRNA','BNTX','GILD','REGN','VRTX','BMY','BIIB','AMGN'] },
  { id:'defense',     name:'Defense',       color:'#64748b', desc:'Aerospace, defense & security',       tickers:['LMT','RTX','NOC','GD','BA','LDOS','HII','TDG'] },
  { id:'telecom',     name:'Telecom',       color:'#38bdf8', desc:'Wireless, internet & cable',          tickers:['T','VZ','TMUS','CHTR','SIRI','DISH','CMCSA','LUMN'] },
  { id:'materials',   name:'Materials',     color:'#d97706', desc:'Metals, mining & chemicals',          tickers:['LIN','APD','NEM','FCX','DOW','DD','ECL','NUE'] },
  { id:'ai',          name:'AI & Semis',    color:'#16a34a', desc:'AI infrastructure & chip makers',     tickers:['NVDA','AMD','AVGO','TSM','MU','SMCI','INTC','QCOM'] },
  { id:'crypto',      name:'Crypto',        color:'#f59e0b', desc:'Bitcoin, Ethereum & top coins',       tickers:['BTC','ETH','SOL','XRP','ADA','DOGE','LTC','LINK'] },
  { id:'cryptostocks',name:'Crypto Stocks', color:'#fbbf24', desc:'Exchanges, miners & custodians',      tickers:['COIN','MSTR','MARA','RIOT','HOOD','CLSK','SQ','PYPL'] },
  { id:'streaming',   name:'Streaming',     color:'#e11d48', desc:'Streaming, media & entertainment',    tickers:['NFLX','DIS','WBD','SPOT','ROKU','CMCSA','FUBO','AMCX'] },
  { id:'intl',        name:'International', color:'#0ea5e9', desc:'Non-US listings & global brands',     tickers:['BABA','TM','NVS','SHOP','ASML','SAP','SONY','BIDU'] },
  // "This week's IPOs" — populated server-side from /api/market/ipos.
  // Tickers are filled at SectorTile render-time when the sector id matches.
  // Empty tickers[] is intentional: the receiver fetches the dynamic list
  // on first render so it stays current without a redeploy. Brand new
  // listings (less than ~30 trading days old) often have no historical
  // sectoral fit — surfacing them in their own card is more useful than
  // mis-categorizing them under tech/finance/etc.
  { id:'ipos',     name:'New IPOs',    color:'#a3e635', desc:'Stocks that went public this week', tickers:[] },
  { id:'commodities', name:'Commodities', color:'#f59e0b', desc:'Gold, silver, oil & more',          tickers:['GC=F','SI=F','CL=F','NG=F','HG=F','BZ=F'] },
];

// Format helpers — STOCKS no longer ships hardcoded prices, so every
// numeric field is undefined until /api/market-stream or /api/market/snapshot
// delivers a quote and applyQuote populates STOCKS[sym]. These helpers
// render a placeholder em-dash when the value isn't there yet so .toFixed()
// never throws on undefined. Pair with the .stale-price-data pulse class
// applied at the parent container — the placeholder is invisible behind
// the blur, but the layout dimensions are correct so nothing reflows when
// real data lands.
function _isLive(sym) {
  return !!(typeof window !== 'undefined' && window.__liveSymbols && window.__liveSymbols.has(sym));
}
function _staleClass(sym, extra) {
  return (_isLive(sym) ? '' : 'stale-price-data') + (extra ? ' ' + extra : '');
}
// Smart decimal precision — small-cap crypto (SHIB ≈ $0.00002, PEPE ≈ $0.000001)
// can't use 2 decimals or they all render as "$0.00". Below 1¢ we fall through
// to 6 decimals; below $1 we use 4 decimals so single-dollar names like XRP
// still get cents-level resolution. Above $1 we keep the existing 2-decimal
// default so AAPL/SPY/etc. don't visually thrash. Caller can still force a
// specific precision by passing `decimals` explicitly.
function _smartDecimals(v) {
  const a = Math.abs(v);
  if (a >= 1)    return 2;
  if (a >= 0.01) return 4;
  if (a >= 0.0001) return 6;
  return 8;
}
function _money(v, decimals, compact) {
  if (!Number.isFinite(v)) return '$—';
  // compact=true: strip decimals on values ≥ $1000 so they fit in narrow
  // 4-column sector cells without overflow. E.g. $4,486 vs $4,485.63.
  const d = (typeof decimals === 'number') ? decimals
    : (compact && Math.abs(v) >= 1000) ? 0
    : _smartDecimals(v);
  const [int, frac] = v.toFixed(d).replace('-','').split('.');
  const intComma = int.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return `$${v < 0 ? '-' : ''}${intComma}${frac !== undefined ? '.' + frac : ''}`;
}
function _pct(v, decimals = 2) {
  return Number.isFinite(v) ? `${v >= 0 ? '+' : ''}${v.toFixed(decimals)}%` : '—';
}
function _absPct(v, decimals = 2) {
  return Number.isFinite(v) ? `${Math.abs(v).toFixed(decimals)}%` : '—';
}
function _signedDelta(v, decimals = 2) {
  return Number.isFinite(v) ? `${v >= 0 ? '+' : ''}${v.toFixed(decimals)}` : '—';
}
function _signedMoney(v, decimals = 2) {
  return Number.isFinite(v) ? `${v >= 0 ? '+' : '-'}$${Math.abs(v).toFixed(decimals)}` : '$—';
}
// "up" defaults to true (green tint) when no data — matches the optimistic
// behavior of the original constants and gives a calmer loading look than
// pre-painting everything red.
function _up(v) { return !Number.isFinite(v) || v >= 0; }

const GAINERS = ['NVDA','GS','META','JPM','XOM','LLY','AVGO','PLTR'];
const LOSERS  = ['TSLA','RIVN','F','UNH','AMD','GM','WBA','BA'];
const RANGES  = ['1D','5D','1M','3M','6M','1Y','5Y'];
const FREE_RANGES = ['1D','5D','1M','3M'];
const FREE_WATCHLIST_MAX = 20;

// Font scale
// row.ticker === row.price so the watchlist row has both on the same baseline
// at the same visual weight (per 2026-05-05 user feedback — they were
// previously misaligned because the price was 3pt larger than the ticker).
const FS = {
  sm: { row:{ticker:24,name:16,price:24,pct:16}, exp:{title:22,stat:15,price:30,meta:13}, sec:{title:18,sub:15}, tab:17, hdr:18 },
  md: { row:{ticker:30,name:18,price:30,pct:17}, exp:{title:28,stat:17,price:38,meta:15}, sec:{title:21,sub:17}, tab:20, hdr:22 },
  lg: { row:{ticker:38,name:22,price:38,pct:21}, exp:{title:36,stat:22,price:48,meta:18}, sec:{title:26,sub:21}, tab:26, hdr:28 },
};

// ── Chart utils ────────────────────────────────────────────────────────────
function seededRand(seed) {
  let s = (seed * 2654435761) >>> 0;
  return () => { s ^= s<<13; s ^= s>>17; s ^= s<<5; return (s>>>0)/0xffffffff; };
}
function genPrices(seed, count, base, vol=0.012) {
  const r = seededRand(seed); const p=[base];
  for(let i=1;i<count;i++) p.push(Math.max(p[i-1]+(r()-0.48)*vol*p[i-1], base*0.4));
  return p;
}
function toPath(prices, w, h, pad=4) {
  const mn=Math.min(...prices), mx=Math.max(...prices), rng=mx-mn||1;
  return prices.map((p,i)=>{
    const x=pad+(i/(prices.length-1))*(w-pad*2);
    const y=pad+(1-(p-mn)/rng)*(h-pad*2);
    return `${i?'L':'M'}${x.toFixed(1)},${y.toFixed(1)}`;
  }).join(' ');
}
function toArea(prices, w, h, pad=4) {
  const ln=toPath(prices,w,h,pad);
  const mn=Math.min(...prices), mx=Math.max(...prices), rng=mx-mn||1;
  const i=prices.length-1;
  const lx=pad+(i/(prices.length-1||1))*(w-pad*2);
  const ly=pad+(1-(prices[i]-mn)/rng)*(h-pad*2);
  return `${ln} L${lx.toFixed(1)},${(h-pad).toFixed(1)} L${pad},${(h-pad).toFixed(1)}Z`;
}

// ── Mini Chart ─────────────────────────────────────────────────────────────
function MiniChart({ticker, w=90, h=32}) {
  const s=STOCKS[ticker]; if(!s) return null;
  const up=_up(s.changePct), col=up?'#16a34a':'#ef4444';
  // s.price may be undefined until applyQuote runs; default the chart's
  // baseline to 100 so the synthesized line still has shape. Real chart
  // data (from /api/quote) will replace genPrices() in a follow-up.
  const prices=useMemo(()=>genPrices(s.seed,60,s.price||100,0.011),[ticker,s.price]);
  const gid=`mg${ticker}`;
  return (
    <svg width={w} height={h} aria-hidden="true" style={{display:'block',flexShrink:0}}>
      <defs><linearGradient id={gid} x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stopColor={col} stopOpacity="0.35"/>
        <stop offset="100%" stopColor={col} stopOpacity="0"/>
      </linearGradient></defs>
      <path d={toArea(prices,w,h)} fill={`url(#${gid})`}/>
      <path d={toPath(prices,w,h)} fill="none" stroke={col} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );
}

// ── Full Chart ─────────────────────────────────────────────────────────────
// X-axis tick generator — returns 4 labels evenly spaced for the given range.
// Mock data generation uses no real timestamps, so the labels describe the
// span (1D = market hours, longer = relative dates). Helps the user orient
// the chart instead of seeing an unlabeled trace. (2026-05-03 user complaint:
// "the x axis is incorrect" — was empty.)
function chartXAxisLabels(range) {
  switch(range) {
    case '1D':  return ['9:30',  '11:30', '1:30',  '4:00'];
    case '5D':  return ['Mon',   'Tue',   'Wed',   'Thu/Fri'];
    case '1M':  return ['-4w',   '-3w',   '-2w',   'now'];
    case '3M':  return ['-3mo',  '-2mo',  '-1mo',  'now'];
    case '6M':  return ['-6mo',  '-4mo',  '-2mo',  'now'];
    case '1Y':  return ['-12mo', '-8mo',  '-4mo',  'now'];
    case '5Y':  return ['-5y',   '-3y',   '-1y',   'now'];
    default:    return ['',      '',      '',      ''];
  }
}

// A chart can briefly unmount and remount when a remote sends repeated OK
// keydowns. Reuse the same authenticated request so that does not become a
// burst of identical /bars calls. This is deliberately a short-lived memory
// cache: changing ticker or range still fetches immediately, and the server
// remains the source of longer-lived market-data caching.
const CHART_BARS_CACHE_TTL_MS = 15000;
const CHART_BARS_CACHE_MAX_ENTRIES = 100;
const chartBarsRequestCache = new Map();

function chartCloses(data) {
  if (!data || !Array.isArray(data.bars) || !data.bars.length) return null;
  const closes = data.bars.map(bar => bar.c).filter(Number.isFinite);
  return closes.length > 1 ? closes : null;
}

function loadChartBars(ticker, range, token) {
  const key = `${token}\u0000${ticker}\u0000${range}`;
  const now = Date.now();
  const cached = chartBarsRequestCache.get(key);
  if (cached) {
    // Join only in-flight work. Replaying completed chart data gives TV users
    // a visible stale graph before the fresh bars land.
    if (cached.promise) return cached.promise;
    chartBarsRequestCache.delete(key);
  }

  if (chartBarsRequestCache.size >= CHART_BARS_CACHE_MAX_ENTRIES) {
    for (const [cacheKey, entry] of chartBarsRequestCache) {
      if (!entry.promise && entry.expiresAt <= now) chartBarsRequestCache.delete(cacheKey);
    }
    if (chartBarsRequestCache.size >= CHART_BARS_CACHE_MAX_ENTRIES) {
      chartBarsRequestCache.delete(chartBarsRequestCache.keys().next().value);
    }
  }

  const headers = { 'Accept': 'application/json', 'Authorization': `Bearer ${token}` };
  // Crypto pairs (BTC/USD) need ?symbol= to skip Apache's %2F rejection.
  const barsParams = new URLSearchParams({ range });
  const barsPath = ticker.includes('/')
    ? '/api/market/bars?' + new URLSearchParams({ symbol: ticker, range }).toString()
    : '/api/market/bars/' + encodeURIComponent(ticker) + '?' + barsParams.toString();
  const entry = { data: null, expiresAt: now + CHART_BARS_CACHE_TTL_MS, promise: null };
  entry.promise = fetch(barsPath, { headers, cache: 'no-store' })
    .then(response => {
      if (!response.ok) throw new Error(`bars request failed (${response.status})`);
      return response.json();
    })
    .then(data => {
      chartBarsRequestCache.delete(key);
      return data;
    })
    .catch(error => {
      if (chartBarsRequestCache.get(key) === entry) chartBarsRequestCache.delete(key);
      throw error;
    });
  chartBarsRequestCache.set(key, entry);
  return entry.promise;
}

function FullChart({ticker, range, fontSize, maxHeight = 200, theme}) {
  // Build a lightweight shell for unknown symbols (e.g. ETFs like SPY/QQQ/VOO)
  // so the real /api/market/bars fetch can still render a chart.
  const s=STOCKS[ticker]||{seed:Array.from(ticker).reduce((a,c)=>a*31+c.charCodeAt(0),7)|0,price:100,changePct:0};
  const th=_th(theme);
  const up=_up(s.changePct), col=up?'#16a34a':'#ef4444';
  // Real OHLC from /api/market/bars/:symbol only. Do not render synthetic or
  // completed cached bars for first paint: on TV that looks like the old graph
  // flashing before the fresh one arrives.
  const chartKey = `${ticker}\u0000${range}`;
  const [chartData, setChartData] = useState(() => {
    const initialToken = (typeof window !== 'undefined' && window.localStorage) ? localStorage.getItem('sc-token') : null;
    return { key: chartKey, prices: null, status: initialToken ? 'loading' : 'failed' };
  });
  useEffect(() => {
    let cancelled = false;
    const tok = (typeof window !== 'undefined' && window.localStorage) ? localStorage.getItem('sc-token') : null;
    if (!tok) {
      setChartData({ key: chartKey, prices: null, status: 'failed' });
      return;
    }
    setChartData({ key: chartKey, prices: null, status: 'loading' });
    loadChartBars(ticker, range, tok)
      .then(j => {
        if (cancelled) return;
        const prices = chartCloses(j);
        setChartData({ key: chartKey, prices, status: prices ? 'ready' : 'failed' });
      })
      .catch(() => {
        if (!cancelled) setChartData({ key: chartKey, prices: null, status: 'failed' });
      });
    return () => { cancelled = true; };
  }, [ticker, range, chartKey]);
  const realPrices = chartData.key === chartKey ? chartData.prices : null;
  if (!realPrices) {
    const loading = chartData.key !== chartKey || chartData.status === 'loading';
    return <div role="status" aria-label={loading?'Loading chart':'Chart currently unavailable'} data-chart-placeholder={loading?'loading':'unavailable'} style={{height:maxHeight,display:'flex',alignItems:'center',justifyContent:'center',gap:10,color:th.sub,fontSize:FS[fontSize||'md'].meta}}>
      {loading && <span aria-hidden="true" style={{width:18,height:18,border:`2px solid ${th.border}`,borderTopColor:th.sub,borderRadius:'50%',display:'inline-block',animation:'spin 0.9s linear infinite'}}/>}
      <span>{loading?'Loading chart…':'Chart currently unavailable'}</span>
    </div>;
  }
  const prices = realPrices;
  // Layout: y-axis labels in HTML (left column), x-axis labels in HTML
  // (bottom row), chart trace stays as SVG. Putting labels OUTSIDE the SVG
  // means they render at native HTML font size regardless of how the SVG
  // scales — fixes the "labels look insane / too tiny / too huge" issue
  // when the chart is in narrow contexts (watchlist sidebar) vs wide
  // (sector overlay). User 2026-05-03 reports.
  const W = 640, H = 200;
  const mn = Math.min(...prices), mx = Math.max(...prices);
  const f = FS[fontSize||'md'].exp;
  // Half size per user 2026-05-03 ("smaller for the axis (i know its gonna
  // be small) half the size"). Was f.meta + 2, now f.meta / 2 — small but
  // legible. HTML text doesn't shrink with SVG so it stays this exact size.
  const labelFs = Math.max(8, Math.round(f.meta / 2));
  const yLabels = [mx, (mn+mx)/2, mn];
  const xLabels = chartXAxisLabels(range);
  const gid = `fg${ticker}${range}`;
  // Map prices to full SVG area (no inset — labels are outside)
  const ptX = i => (i / (prices.length - 1)) * W;
  const ptY = p => (1 - (p - mn) / (mx - mn || 1)) * H;
  const svgCmd = (p, i) => (i === 0 ? 'M' : 'L') + ptX(i).toFixed(1) + ' ' + ptY(p).toFixed(1);
  const linePath = prices.map(svgCmd).join(' ');
  const areaPath = `${linePath} L${W} ${H} L0 ${H} Z`;
  const _numBase = v => (v >= 100 ? v.toFixed(0) : v.toFixed(2));
  const _numDupe = new Set(yLabels.map(_numBase)).size < yLabels.length;
  const numFmt = v => (_numDupe ? v.toFixed(2) : _numBase(v));
  return (
    <div style={{display:'flex',gap:8,alignItems:'stretch'}}>
      {/* Y-axis labels — HTML column, native font size */}
      <div style={{display:'flex',flexDirection:'column',justifyContent:'space-between',fontSize:labelFs,color:th.sub,fontFamily:'DM Mono,monospace',textAlign:'right',minWidth:60,paddingTop:2,paddingBottom:labelFs+8}}>
        {yLabels.map((v, i) => <span key={i}>{'$'}{numFmt(v)}</span>)}
      </div>
      <div style={{flex:1,minWidth:0}} data-chart-source="market-bars" data-chart-ticker={ticker} data-chart-range={range}>
        <svg width={W} height={H} viewBox={`0 0 ${W} ${H}`} aria-hidden="true" preserveAspectRatio="none" style={{display:'block',width:'100%',height:'auto',maxHeight}}>
          <defs><linearGradient id={gid} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={col} stopOpacity="0.28"/>
            <stop offset="100%" stopColor={col} stopOpacity="0"/>
          </linearGradient></defs>
          {/* Horizontal gridlines at y-tick positions */}
          {[0, 0.5, 1].map((tick, i) => (
            <line key={i} x1={0} y1={tick * H} x2={W} y2={tick * H}
              stroke={th.gridLine} strokeWidth="1"/>
          ))}
          <path d={areaPath} fill={`url(#${gid})`}/>
          <path d={linePath} fill="none" stroke={col} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" vectorEffect="non-scaling-stroke"/>
        </svg>
        {/* X-axis labels — HTML row, native font size */}
        <div style={{display:'flex',justifyContent:'space-between',fontSize:labelFs,color:th.sub,fontFamily:'DM Mono,monospace',marginTop:4,paddingLeft:2,paddingRight:2}}>
          {xLabels.map((label, i) => <span key={i}>{label}</span>)}
        </div>
      </div>
    </div>
  );
}

// ── Stock Row ──────────────────────────────────────────────────────────────
// `alerts` prop: full alerts array (filtered down internally). Used to render
// a 🔔 badge on the row when any alert for this ticker has fired in the last
// 24h. Without this, a triggered alert was invisible on the watchlist row —
// users had to open ExpandedStock to know an alert existed at all (2026-05-02
// bugbash P2-4).
function StockRow({ticker, selected, onClick, fontSize, accentColor, alerts, onMarkSeen, theme, compact}) {
  const th=_th(theme);
  const s=STOCKS[ticker];
  const f=FS[fontSize].row;
  // Skeleton row — no STOCKS data yet (typical: just-added via search,
  // snapshot fetch in flight). Show the ticker symbol + a stale-pulsing
  // dash so the user knows the add took, instead of returning null and
  // having the new row pop in 5-10s later (2026-05-07 user report:
  // "stocks added in search don't immediately show up").
  if(!s) {
    return (
      <div className={`stock-row${selected?' selected':''}`} onClick={onClick}
        role="button" tabIndex={0} aria-label={`${ticker} loading stock quote`}
        onKeyDown={e=>{if((e.key==='Enter'||e.key===' '||e.keyCode===13)&&!e.repeat){e.preventDefault();onClick&&onClick(e);}}}
        style={{display:'flex',alignItems:'flex-start',gap:compact?6:14,padding:compact?'8px 10px':'13px 20px',cursor:'pointer',
          borderLeft:`5px solid ${selected?accentColor:'transparent'}`,
          background:selected?`${accentColor}40`:'transparent',
          transition:'all 0.18s',borderRadius:4,opacity:0.85}}>
        <div style={{flex:1,minWidth:0}}>
          <div style={{fontSize:f.ticker,fontWeight:700,lineHeight:1.1}}>{ticker}</div>
          <div style={{fontSize:f.name,color:th.sub,marginTop:4}}>Loading…</div>
        </div>
        <div style={{textAlign:'right',minWidth:compact?62:170,whiteSpace:'nowrap'}}>
          <div className="stale-price-data" style={{fontSize:f.price,fontWeight:600,fontFamily:'DM Mono,monospace',lineHeight:1.1}}>—</div>
          <div className="stale-price-data" style={{fontSize:f.pct,color:th.sub,fontFamily:'DM Mono,monospace',marginTop:4}}>—</div>
        </div>
      </div>
    );
  }
  const up=_up(s.changePct), col=up?'#16a34a':'#ef4444';
  const stale=_staleClass(ticker);
  // recentlyFired: any alert for this ticker fired in the last 24h AND
  // not yet marked seen by the user. The "seen" state lives in
  // window.__sc_alertsSeen (Map of alertId → seenAt) — populated by
  // the Mark Seen button below.
  const seenSet = (typeof window !== 'undefined' && window.__sc_alertsSeen) || null;
  const firedAlerts = Array.isArray(alerts) ? alerts.filter(a =>
    a.symbol === ticker
    && a.lastTriggeredAt
    && (Date.now() - Number(a.lastTriggeredAt)) < 24*3600*1000
    && !(seenSet && seenSet.has(a.id))
  ) : [];
  const recentlyFired = firedAlerts.length > 0;
  const firedAgoMs = recentlyFired ? Date.now() - Math.max(...firedAlerts.map(a => Number(a.lastTriggeredAt))) : 0;
  const firedAgoStr = firedAgoMs < 60_000 ? 'just now' : firedAgoMs < 3_600_000 ? `${Math.round(firedAgoMs/60_000)}m ago` : `${Math.round(firedAgoMs/3_600_000)}h ago`;
  const handleMarkSeen = (e) => {
    e.stopPropagation();
    if (!window.__sc_alertsSeen) window.__sc_alertsSeen = new Map();
    firedAlerts.forEach(a => window.__sc_alertsSeen.set(a.id, Date.now()));
    if (onMarkSeen) onMarkSeen(firedAlerts.map(a => a.id));
  };
  const compactPriceWidth = compact ? (fontSize === 'lg' ? 166 : fontSize === 'sm' ? 112 : 140) : 170;
  return (
    <div ref={selected?(el=>{el&&el.scrollIntoView&&el.scrollIntoView({block:'nearest',behavior:'smooth'});})  :null}
      className={`stock-row${selected?' selected':''}${recentlyFired?' alert-fired':''}`} onClick={onClick}
      role="button" tabIndex={0} aria-label={`${ticker} ${s.name}`}
      onKeyDown={e=>{if((e.key==='Enter'||e.key===' '||e.keyCode===13)&&!e.repeat){e.preventDefault();onClick&&onClick(e);}}}
      style={{display:'flex',alignItems:'flex-start',gap:compact?6:14,padding:compact?'8px 10px':'13px 20px',cursor:'pointer',
        borderLeft:`5px solid ${selected?accentColor:'transparent'}`,
        background:selected?`${accentColor}22`:'transparent',
        boxShadow:selected?`0 0 0 3px ${accentColor}, 0 0 16px ${accentColor}66`:'none',
        transition:'all 0.15s ease-out',borderRadius:6,position:selected?'relative':'static',zIndex:selected?2:1}}>
      <div style={{flex:1,minWidth:0,overflow:'hidden'}}>
        <div style={{fontSize:f.ticker,fontWeight:700,display:'flex',alignItems:'center',gap:8,lineHeight:1.1,minWidth:0}}>
          <span style={{minWidth:0,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{ticker}</span>
          {recentlyFired && <span title={`Alert fired ${firedAgoStr}`} style={{fontSize:f.ticker-8,color:'#fbbf24',background:'rgba(251,191,36,0.18)',border:'1px solid rgba(251,191,36,0.4)',borderRadius:8,padding:'1px 7px',lineHeight:1.2,fontWeight:700}}>🔔 {firedAgoStr}</span>}
          {recentlyFired && <button onClick={handleMarkSeen} aria-label="Mark alert seen" style={{fontSize:f.ticker-8,background:'transparent',border:`1px solid ${th.border}`,color:th.sub,borderRadius:6,padding:'1px 7px',cursor:'pointer',fontFamily:'Space Grotesk,sans-serif',whiteSpace:'nowrap'}}>✓ Seen</button>}
        </div>
        <div style={{fontSize:f.name,color:th.sub,marginTop:4,whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis'}}>{s.name}</div>
      </div>
      {/* Per-line .stale-price-data: only the price/change/pct numbers
          pulse-blur until applyQuote runs. Ticker symbol + name stay
          crisp so the row still reads as "Apple Inc. (AAPL)" while
          waiting for the first quote.
          Layout (2026-05-05): ticker and price share the top baseline at
          the same font size; signed-dollar + percent collapse to a single
          row below to save vertical space. */}
      <div style={{textAlign:'right',width:compactPriceWidth,minWidth:compactPriceWidth,maxWidth:compactPriceWidth,whiteSpace:'nowrap',overflow:'hidden',flexShrink:0}}>
        {/* Fix 1: min-width:7ch reserves enough room for "$999.99" so the cell
            doesn't expand when real price data arrives (CLS fix 2026-05-15). */}
        <div className={stale} style={{fontSize:f.price,fontWeight:600,fontFamily:'DM Mono,monospace',lineHeight:1.1,minWidth:'7ch',maxWidth:'100%',display:'inline-block',textAlign:'right',overflow:'hidden',textOverflow:'clip'}}>{_money(s.price, undefined, compact)}</div>
        <div className={stale} style={{fontSize:f.pct,color:col,fontFamily:'DM Mono,monospace',marginTop:4,whiteSpace:'nowrap',overflow:'hidden',textOverflow:'clip'}}>{_signedMoney(s.change)} {_pct(s.changePct)}</div>
      </div>
    </div>
  );
}

// ── News list ──────────────────────────────────────────────────────────────
// Real headlines from /api/market/news/:symbol. Backend hits Finnhub
// (primary, real publishers) → Marketaux (backup), 30-min cache. Each
// item carries { headline, source, url, publishedAt, summary, image }.
// We display source name + relative time + headline; tap-to-phone QR
// would route to `url` in a future iteration (no click-through on TV
// today). Source attribution is required by both providers' ToS.
function _agoSec(epochSec) {
  if (!epochSec) return '';
  const dt = Math.max(0, Math.floor(Date.now()/1000) - epochSec);
  if (dt < 60) return `${dt}s`;
  if (dt < 3600) return `${Math.floor(dt/60)}m`;
  if (dt < 86400) return `${Math.floor(dt/3600)}h`;
  return `${Math.floor(dt/86400)}d`;
}
function NewsList({ ticker, fontSize, accentColor, max = 2, showHeader = false, theme }) {
  const th=_th(theme);
  const f = FS[fontSize].exp;
  const [state, setState] = React.useState({ items: [], status: 'loading', source: null });
  React.useEffect(() => {
    let cancelled = false;
    if (!ticker) { setState({ items: [], status: 'empty', source: null }); return; }
    setState(s => ({ ...s, status: 'loading' }));
    const tok = (typeof window !== 'undefined' && window.localStorage) ? localStorage.getItem('sc-token') : null;
    const headers = { 'Accept': 'application/json' };
    if (tok) headers['Authorization'] = `Bearer ${tok}`;
    fetch(`/api/market/news/${encodeURIComponent(ticker)}`, { headers, credentials: 'same-origin', cache: 'no-store' })
      .then(r => r.ok ? r.json() : Promise.reject(new Error(`http_${r.status}`)))
      .then(data => {
        if (cancelled) return;
        const items = Array.isArray(data?.items) ? data.items.slice(0, max) : [];
        setState({ items, status: items.length ? 'ok' : 'empty', source: data?.source || null });
      })
      .catch(() => { if (!cancelled) setState({ items: [], status: 'error', source: null }); });
    return () => { cancelled = true; };
  }, [ticker, max]);
  if (state.status === 'loading') {
    return (<div style={{fontSize:f.meta,color:th.sub,padding:'4px 0'}}>Loading headlines…</div>);
  }
  if (state.status !== 'ok') {
    return null;
  }
  return (
    <div style={{display:'flex',flexDirection:'column',gap:6}}>
      {showHeader&&<div style={{fontSize:f.meta,color:th.sub,marginBottom:4,textTransform:'uppercase',letterSpacing:'0.08em',fontWeight:600}}>Top Headlines</div>}
      {state.items.map((n, i) => (
        <div key={n.url || i} style={{display:'flex',gap:10,alignItems:'stretch',background:th.gridLine,border:`1px solid ${th.border}`,borderRadius:8,padding:'8px 12px'}}>
          <div style={{width:3,borderRadius:2,background:accentColor||'#3b82f6',flexShrink:0}}/>
          <div style={{flex:1,minWidth:0}}>
            <div style={{display:'flex',gap:8,fontSize:f.meta,color:th.sub,marginBottom:3}}>
              <span style={{fontWeight:600,color:th.text}}>via {n.source}</span>
              {n.publishedAt ? (<><span>·</span><span>{_agoSec(n.publishedAt)} ago</span></>) : null}
            </div>
            <div style={{fontSize:f.stat,fontWeight:600,lineHeight:1.35,overflow:'hidden',whiteSpace:'nowrap'}}><span className="headline-scroll">{n.headline}</span></div>
          </div>
        </div>
      ))}
    </div>
  );
}

// AlertPanel — collapsed by default to a small "🔔 Set alert" button.
// Click expands to the full Low/High/Save form. Existing alert chips
// always render (compact summary). Halves the space the alerts panel
// took before — user 2026-05-03 "the whole box doesnt need to be visible".
function AlertPanel({ ticker, alerts, onAddAlert, onDeleteAlert, accentColor, fontSize, isPro, onPaywall, theme }) {
  const th=_th(theme);
  const f = FS[fontSize].exp;
  const tickerAlerts = Array.isArray(alerts) ? alerts : [];
  const [open, setOpen] = React.useState(false);
  const [lowInput, setLowInput] = React.useState('');
  const [highInput, setHighInput] = React.useState('');
  const [saving, setSaving] = React.useState(false);
  const [saveErr, setSaveErr] = React.useState(null);
  const lowRef = React.useRef(null);
  const highRef = React.useRef(null);
  const saveRef = React.useRef(null);
  const cancelRef = React.useRef(null);
  const canSave = (!!lowInput.trim() || !!highInput.trim()) && !saving && typeof onAddAlert === 'function';
  const stopNav = e => {
    e.preventDefault();
    if (e.nativeEvent && e.nativeEvent.stopImmediatePropagation) e.nativeEvent.stopImmediatePropagation();
  };
  const handleSave = async () => {
    if (!canSave) return;
    const lo = lowInput.trim() ? Number(lowInput) : null;
    const hi = highInput.trim() ? Number(highInput) : null;
    setSaving(true);
    setSaveErr(null);
    try {
      // onAddAlert returns {ok:true} on success, {ok:false,error?:string}
      // on failure — surface the server's reason (e.g. the 100-alert cap)
      // so the user knows why Save did nothing.
      const res = await onAddAlert(ticker, lo, hi);
      const ok = res === true || (res && res.ok === true);
      if (ok) {
        setLowInput(''); setHighInput(''); setOpen(false);
      } else if (res && res.error) {
        setSaveErr(res.error);
      } else if (res === false) {
        setSaveErr('Could not save alert');
      }
    } finally { setSaving(false); }
  };
  if (typeof onAddAlert !== 'function') return null;
  // Free-tier upsell: matches the backend gate at /api/alerts. Pricing
  // page lists "Price alerts" only under Pro/Pro+, so render a small
  // upgrade pill instead of the form for free users (was silently
  // 403-failing on Save before, with no UI feedback). 2026-05-03.
  if (!isPro) {
    return (
      <button onClick={() => onPaywall && onPaywall('Price Alerts')}
        tabIndex={0}
        style={{background:'rgba(245,158,11,0.10)',border:'1px solid rgba(245,158,11,0.35)',borderRadius:8,padding:'5px 12px',fontSize:f.meta,fontWeight:600,color:'#f59e0b',cursor:'pointer',fontFamily:'Space Grotesk,sans-serif',display:'inline-flex',alignItems:'center',gap:6}}>
        🔔 <span>Set alert · </span><span style={{fontWeight:700}}>Pro</span>
      </button>
    );
  }
  return (
    <div style={{display:'flex',alignItems:'center',gap:8,flexWrap:'wrap'}}>
      {/* Existing alerts: compact chips, always visible */}
      {tickerAlerts.map(a => {
        const parts = [];
        if (a.highPrice != null) parts.push(`≥ $${Number(a.highPrice).toFixed(2)}`);
        if (a.lowPrice  != null) parts.push(`≤ $${Number(a.lowPrice).toFixed(2)}`);
        return (
          <div key={a.id} style={{display:'inline-flex',alignItems:'center',gap:6,background:th.gridLine,border:`1px solid ${th.border}`,borderRadius:8,padding:'4px 6px 4px 10px',fontSize:f.meta,fontFamily:'DM Mono,monospace',color:th.text}}>
            <span>🔔 {parts.join(' · ')}</span>
            <button onClick={()=>onDeleteAlert && onDeleteAlert(a.id)} aria-label="Delete alert"
              tabIndex={0}
              style={{background:'transparent',border:'none',color:th.sub,cursor:'pointer',fontSize:f.meta+2,lineHeight:1,padding:'0 4px'}}>×</button>
          </div>
        );
      })}
      {/* Collapsed: small "Set alert" button. Expanded: inline form. */}
      {!open ? (
        <button onClick={()=>setOpen(true)}
          aria-expanded={open}
          tabIndex={0}
          style={{background:`${accentColor}18`,border:`1px solid ${accentColor}55`,borderRadius:8,padding:'5px 12px',fontSize:f.meta,fontWeight:600,color:accentColor,cursor:'pointer',fontFamily:'Space Grotesk,sans-serif'}}>
          🔔 {tickerAlerts.length > 0 ? '+ Add alert' : 'Set alert'}
        </button>
      ) : (
        <div style={{display:'inline-flex',alignItems:'center',gap:6,background:th.gridLine,border:`1px solid ${th.border}`,borderRadius:8,padding:'4px 6px 4px 10px'}}>
          <span style={{fontSize:f.meta,color:th.sub}}>Low $</span>
          <input ref={lowRef} type="number" inputMode="decimal" step="0.01" min="0" value={lowInput} autoFocus
            onChange={e=>setLowInput(e.target.value)}
            onKeyDown={e=>{
              if(e.key==='Enter'&&canSave){e.preventDefault();handleSave();return;}
              if(e.key==='ArrowDown'||e.key==='ArrowRight'||e.keyCode===40||e.keyCode===39){stopNav(e);highRef.current?.focus();}
            }}
            placeholder="—"
            style={{width:64,background:th.overlay,border:`1px solid ${th.border}`,borderRadius:6,padding:'4px 6px',color:th.text,fontFamily:'DM Mono,monospace',fontSize:f.meta}}/>
          <span style={{fontSize:f.meta,color:th.sub}}>High $</span>
          <input ref={highRef} type="number" inputMode="decimal" step="0.01" min="0" value={highInput}
            onChange={e=>setHighInput(e.target.value)}
            onKeyDown={e=>{
              if(e.key==='Enter'&&canSave){e.preventDefault();handleSave();return;}
              if(e.key==='ArrowUp'||e.key==='ArrowLeft'||e.keyCode===38||e.keyCode===37){stopNav(e);lowRef.current?.focus();return;}
              if(e.key==='ArrowDown'||e.key==='ArrowRight'||e.keyCode===40||e.keyCode===39){stopNav(e);saveRef.current?.focus();}
            }}
            placeholder="—"
            style={{width:64,background:th.overlay,border:`1px solid ${th.border}`,borderRadius:6,padding:'4px 6px',color:th.text,fontFamily:'DM Mono,monospace',fontSize:f.meta}}/>
          <button ref={saveRef} onClick={handleSave} disabled={!canSave}
            onKeyDown={e=>{
              if(e.key==='ArrowUp'||e.key==='ArrowLeft'||e.keyCode===38||e.keyCode===37){stopNav(e);highRef.current?.focus();return;}
              if(e.key==='ArrowRight'||e.keyCode===39){stopNav(e);cancelRef.current?.focus();}
            }}
            tabIndex={0}
            style={{background:canSave?accentColor:th.border,color:canSave?'#fff':th.sub,border:'none',padding:'4px 10px',fontSize:f.meta,fontWeight:600,borderRadius:6,cursor:canSave?'pointer':'default',fontFamily:'Space Grotesk,sans-serif'}}>
            {saving?'…':'Save'}
          </button>
          <button ref={cancelRef} onClick={()=>{setOpen(false);setLowInput('');setHighInput('');setSaveErr(null);}} aria-label="Cancel"
            onKeyDown={e=>{if(e.key==='ArrowLeft'||e.key==='ArrowUp'||e.keyCode===37||e.keyCode===38){stopNav(e);saveRef.current?.focus();}}}
            tabIndex={0}
            style={{background:'transparent',border:'none',color:th.sub,cursor:'pointer',fontSize:f.meta+2,lineHeight:1,padding:'0 4px'}}>×</button>
          {saveErr && (
            <div role="alert" style={{flexBasis:'100%',marginTop:6,fontSize:f.meta,color:'#ef4444',fontFamily:'Space Grotesk,sans-serif'}}>
              {saveErr}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function ExpandedStock({ticker, range, onRangeChange, fontSize, accentColor, isPro, alerts, onAddAlert, onDeleteAlert, compact, onPaywall, theme}) {
  const th=_th(theme);
  const s=STOCKS[ticker]; if(!s) return null;
  const up=_up(s.changePct), col=up?'#16a34a':'#ef4444';
  const f=FS[fontSize].exp;
  const sector=ALL_SECTORS.find(x=>x.id===s.sector);
  const stale=_staleClass(ticker);
  // Layout-mode prop:
  //   compact=undefined/false (default, watchlist context) → ORIGINAL vertical
  //     stack (chart, range buttons, alerts, 52W grid, headlines). Watchlist
  //     sidebar is narrow ~440px so a 2-col layout cramps everything.
  //   compact=true (sector context, with wider overlay) → 2-col layout
  //     (chart left, alerts/52W/headlines right). Halves vertical height.
  // 2026-05-03 user clarification: keep watchlist as-is; only sectors get
  // the 2-col treatment.
  if (!compact) {
    // ORIGINAL vertical layout (watchlist) — restored 2026-05-03.
    return (
      <div style={{background:th.surface,border:`1px solid ${accentColor}33`,borderRadius:12,padding:'18px 20px',margin:'2px 0 6px'}}>
        {sector&&(
          <div style={{marginBottom:10}}>
            <span style={{color:th.sub,fontSize:f.meta,fontWeight:500,background:th.gridLine,padding:'2px 8px',borderRadius:12}}>{sector.name}</span>
          </div>
        )}
        <FullChart ticker={ticker} range={range} fontSize={fontSize} theme={theme}/>
        <div style={{display:'flex',gap:4,marginTop:14,flexWrap:'nowrap',overflow:'hidden'}}>
          {RANGES.map(r=>{
            const locked=!isPro&&!FREE_RANGES.includes(r);
            return (
              <button key={r} onClick={()=>locked?(onPaywall&&onPaywall('Long-range Charts')):onRangeChange(r)}
                style={{background:range===r?accentColor:locked?th.border:'rgba(127,127,127,0.12)',
                  color:range===r?'#fff':locked?th.sub:th.sub,
                  border:`1px solid ${locked?th.border:'transparent'}`,
                  padding:'4px 8px',fontSize:f.meta,fontWeight:500,borderRadius:6,cursor:'pointer',
                  fontFamily:'Space Grotesk,sans-serif',display:'flex',alignItems:'center',gap:3,flexShrink:0,whiteSpace:'nowrap'}}>
                {locked&&<span style={{fontSize:f.meta-3}}>🔒</span>}{r}
              </button>
            );
          })}
        </div>
        {/* 2x2 grid in the watchlist sidebar — the right panel is ~440px
            wide; 4 cards in a row clipped Mkt Cap on real TV (2026-05-05). */}
        <div style={{display:'grid',gridTemplateColumns:'repeat(2,minmax(0,1fr))',gap:10,marginTop:16}}>
          {[{l:'52W High',v:_money(s.high52)},{l:'52W Low',v:_money(s.low52)},{l:'Volume',v:s.vol||'—'},{l:'Mkt Cap',v:s.mktCap||'—'}].map(({l,v})=>(
            <div key={l} style={{background:th.gridLine,borderRadius:8,padding:'10px 14px',minWidth:0}}>
              <div style={{fontSize:f.meta,color:th.sub,marginBottom:4}}>{l}</div>
              <div className={stale} style={{fontSize:f.stat,fontWeight:600,fontFamily:'DM Mono,monospace',color:th.text,whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis'}}>{v}</div>
            </div>
          ))}
        </div>
        <div style={{marginTop:16}}>
          <NewsList ticker={ticker} fontSize={fontSize} accentColor={accentColor} max={2} showHeader={true} theme={theme}/>
        </div>
        {/* AlertPanel moved to bottom (below news) per user 2026-05-03 */}
        <div style={{marginTop:14}}>
          <AlertPanel ticker={ticker} alerts={alerts} onAddAlert={onAddAlert} onDeleteAlert={onDeleteAlert} accentColor={accentColor} fontSize={fontSize} isPro={isPro} onPaywall={onPaywall} theme={theme}/>
        </div>
      </div>
    );
  }
  // compact=true: full-width chart, then 2 paired rows of [headline | box | box].
  // Tightened per 2026-05-06 user request — chart 200→160, boxes minimized.
  const stats = [
    {l:'52W High',v:_money(s.high52)},
    {l:'52W Low', v:_money(s.low52)},
    {l:'Volume',  v:s.vol||'—'},
    {l:'Mkt Cap', v:s.mktCap||'—'},
  ];
  return (
    <div style={{background:th.surface,border:`1px solid ${accentColor}33`,borderRadius:12,padding:'14px',margin:'2px 0 6px',minWidth:0,overflow:'hidden'}}>
      {sector&&(
        <div style={{marginBottom:8}}>
          <span style={{color:th.sub,fontSize:f.meta,fontWeight:500,background:th.gridLine,padding:'2px 8px',borderRadius:12}}>{sector.name}</span>
        </div>
      )}
      {/* Full-width chart, slightly shorter than 200 default. Cap the SVG
          itself at 160 (via maxHeight prop) instead of clipping the parent —
          a clip-style wrapper hid the bottom of the trace where the lowest
          prices live (2026-05-07 user "graph dips below the x axis"). */}
      <FullChart ticker={ticker} range={range} fontSize={fontSize} maxHeight={160} theme={theme}/>
      {/* Range pills */}
      <div style={{display:'flex',gap:3,marginTop:10,flexWrap:'nowrap',overflow:'hidden'}}>
        {RANGES.map(r=>{
          const locked=!isPro&&!FREE_RANGES.includes(r);
          return (
            <button key={r} onClick={()=>locked?(onPaywall&&onPaywall('Long-range Charts')):onRangeChange(r)}
              style={{background:range===r?accentColor:locked?th.border:'rgba(127,127,127,0.12)',
                color:range===r?'#fff':locked?th.sub:th.sub,
                border:`1px solid ${locked?th.border:'transparent'}`,
                padding:'4px 8px',fontSize:f.meta,fontWeight:500,borderRadius:6,cursor:locked?'default':'pointer',
                fontFamily:'Space Grotesk,sans-serif',display:'flex',alignItems:'center',gap:3,flexShrink:0,whiteSpace:'nowrap'}}>
              {locked&&<span style={{fontSize:f.meta-3}}>🔒</span>}{r}
            </button>
          );
        })}
      </div>
      {/* 1x4 stats row directly under the chart (52W H / 52W L / Vol / Mkt Cap)
          — Scott requested 2026-05-19: "use a 1x4 under the graph for high
          low etc". The earlier 2-row [headline | box | box] PairedNewsStats
          layout was too tall + visually crashed when paywall overlapped. */}
      <div style={{marginTop:10,display:'grid',gridTemplateColumns:'repeat(4,minmax(0,1fr))',gap:6}}>
        {stats.map(({l,v}) => (
          <div key={l} style={{background:th.gridLine,borderRadius:6,padding:'5px 8px',minWidth:0,lineHeight:1.05}}>
            <div style={{fontSize:Math.max(8,f.meta-3),color:th.sub,textTransform:'uppercase',letterSpacing:'0.04em',fontWeight:600}}>{l}</div>
            <div className={stale} style={{fontSize:Math.max(11,f.stat-1),fontWeight:600,fontFamily:'DM Mono,monospace',color:th.text,whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis'}}>{v}</div>
          </div>
        ))}
      </div>
      {/* AlertPanel — chip + Set alert button at bottom (preserved) */}
      <div style={{marginTop:10}}>
        <AlertPanel ticker={ticker} alerts={alerts} onAddAlert={onAddAlert} onDeleteAlert={onDeleteAlert} accentColor={accentColor} fontSize={fontSize} isPro={isPro} onPaywall={onPaywall} theme={theme}/>
      </div>
    </div>
  );
}

// PairedNewsStats — 2 rows of [headline | box | box]. Used by ExpandedStock's
// compact branch (gainers/losers, sector overlay). Headlines fetched via
// /api/market/news/:ticker (same shape as NewsList). If <2 headlines, the
// missing rows show "No news" or "Loading…" placeholder for the headline cell.
function PairedNewsStats({ticker, stats, fontSize, accentColor, theme}) {
  const th=_th(theme);
  const f = FS[fontSize].exp;
  const [items, setItems] = React.useState([]);
  const [status, setStatus] = React.useState('loading');
  React.useEffect(() => {
    let cancelled = false;
    if (!ticker) { setStatus('empty'); return; }
    setStatus('loading');
    const tok = (typeof window !== 'undefined' && window.localStorage) ? localStorage.getItem('sc-token') : null;
    const headers = { 'Accept': 'application/json' };
    if (tok) headers['Authorization'] = `Bearer ${tok}`;
    fetch(`/api/market/news/${encodeURIComponent(ticker)}`, { headers, credentials: 'same-origin', cache: 'no-store' })
      .then(r => r.ok ? r.json() : Promise.reject(new Error(`http_${r.status}`)))
      .then(data => {
        if (cancelled) return;
        const arr = Array.isArray(data?.items) ? data.items.slice(0, 2) : [];
        setItems(arr);
        setStatus(arr.length ? 'ok' : 'empty');
      })
      .catch(() => { if (!cancelled) setStatus('error'); });
    return () => { cancelled = true; };
  }, [ticker]);
  return (
    <div style={{marginTop:10,display:'flex',flexDirection:'column',gap:6}}>
      {[0, 1].map(rowIdx => {
        const item = items[rowIdx];
        const box1 = stats[rowIdx * 2];
        const box2 = stats[rowIdx * 2 + 1];
        // Headline gets the lion's share of the row width — boxes shrink so
        // the headline isn't truncated mid-word ("hat's going on…" bug 2026-05-06).
        return (
          <div key={rowIdx} style={{display:'grid',gridTemplateColumns:'minmax(0,2.6fr) minmax(0,0.85fr) minmax(0,0.85fr)',gap:6,alignItems:'stretch'}}>
            {/* Headline cell */}
            <div style={{display:'flex',gap:8,alignItems:'stretch',background:th.gridLine,border:`1px solid ${th.border}`,borderRadius:7,padding:'8px 12px',minWidth:0}}>
              <div style={{width:3,borderRadius:2,background:accentColor||'#3b82f6',flexShrink:0}}/>
              <div style={{flex:1,minWidth:0}}>
                {item ? (
                  <>
                    <div style={{display:'flex',gap:5,fontSize:Math.max(9,f.meta-2),color:th.sub,marginBottom:2}}>
                      <span style={{fontWeight:600,color:th.text}}>via {item.source}</span>
                      {item.publishedAt ? (<><span>·</span><span>{_agoSec(item.publishedAt)} ago</span></>) : null}
                    </div>
                    <div style={{fontSize:Math.max(11,f.stat-1),fontWeight:600,lineHeight:1.3,overflow:'hidden',whiteSpace:'nowrap'}}>
                      <span className="headline-scroll">{item.headline}</span>
                    </div>
                  </>
                ) : (
                  <div style={{fontSize:f.meta,color:th.sub,display:'flex',alignItems:'center',height:'100%'}}>
                    {status === 'loading' ? 'Loading…' : 'No news'}
                  </div>
                )}
              </div>
            </div>
            {/* Stat box 1 — original font sizes; just tighter padding */}
            <div style={{background:th.gridLine,borderRadius:6,padding:'3px 8px',minWidth:0,display:'flex',flexDirection:'column',justifyContent:'center',lineHeight:1.05}}>
              <div style={{fontSize:Math.max(8,f.meta-3),color:th.sub,textTransform:'uppercase',letterSpacing:'0.04em',fontWeight:600}}>{box1.l}</div>
              <div style={{fontSize:Math.max(11,f.stat-1),fontWeight:600,fontFamily:'DM Mono,monospace',color:th.text,whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis'}}>{box1.v}</div>
            </div>
            {/* Stat box 2 */}
            <div style={{background:th.gridLine,borderRadius:6,padding:'3px 8px',minWidth:0,display:'flex',flexDirection:'column',justifyContent:'center',lineHeight:1.05}}>
              <div style={{fontSize:Math.max(8,f.meta-3),color:th.sub,textTransform:'uppercase',letterSpacing:'0.04em',fontWeight:600}}>{box2.l}</div>
              <div style={{fontSize:Math.max(11,f.stat-1),fontWeight:600,fontFamily:'DM Mono,monospace',color:th.text,whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis'}}>{box2.v}</div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ── Dwell-and-page hook ───────────────────────────────────────────────────
// For a long list, dwell on a "page" of `pageSize` items for `dwellMs`,
// then ease-scroll to the next page over `transitionMs`. Loops back to 0.
// Skips pagination entirely when overflow ≤ `overflowGrace` items
// (a 14-item list with pageSize 12 isn't worth flipping for two extras).
// `paused` halts auto-rotation. `forcedPage` (when non-null) snaps to that
// page — used so D-pad focus drags the visible page along with the user.
function useDwellPager({ count, pageSize, dwellMs = 12000, transitionMs = 400, overflowGrace = 2, paused = false, forcedPage = null }) {
  const totalPages = Math.max(1, Math.ceil(count / pageSize));
  const paginate = count > pageSize + overflowGrace;
  const [page, setPage] = React.useState(0);
  React.useEffect(() => {
    if (!paginate || paused || forcedPage != null) return;
    const t = setInterval(() => {
      setPage(p => (p + 1) % totalPages);
    }, dwellMs);
    return () => clearInterval(t);
  }, [paginate, totalPages, dwellMs, paused, forcedPage]);
  React.useEffect(() => {
    if (forcedPage != null) {
      const clamped = Math.min(Math.max(0, forcedPage), Math.max(0, totalPages - 1));
      setPage(clamped);
    }
  }, [forcedPage, totalPages]);
  React.useEffect(() => {
    // If items shrink below current page, snap back.
    if (page >= totalPages) setPage(0);
  }, [page, totalPages]);
  return { page: paginate ? page : 0, totalPages: paginate ? totalPages : 1, paginate, transitionMs };
}

// ── Sector Cluster ─────────────────────────────────────────────────────────
// `sector` (object) takes precedence over `sectorId` (string). Custom-sector
// callers pass the resolved object since custom IDs aren't in ALL_SECTORS.
function SectorCluster({sectorId, sector: sectorProp, fontSize, cols, onStockClick, focusedTicker, focusedSectorId, accentColor, expandedTicker, expandedSectorId, expandedRange, onRangeChange, isPro, alerts, onAddAlert, onDeleteAlert, colIndex, gridCols, onPaywall, theme}) {
  const sector=sectorProp||ALL_SECTORS.find(s=>s.id===sectorId); if(!sector) return null;
  const validTickers=(sector.tickers||[]).filter(t=>STOCKS[t]);
  if(!validTickers.length) return null;
  const th=_th(theme);
  const f=FS[fontSize].sec;
  const nCols=cols||2;
  // Density: 3 rows max so a 7-ticker sector (Tech) doesn't push 4 rows
  // and crash the layout. Combined with the vertical stacking in narrow
  // cells (3+ cols), each cell can comfortably show ticker + price + pct
  // without horizontal collision. (User report 2026-05-07: "when going
  // to 4 rows, the name and the price crash" + "not all sectors have 8
  // stocks".)
  // 2 rows of stocks: 2 cols → 4, 3 cols → 6, 4 cols → 8.
  // Scott 2026-05-19: previous 3-row cap (8 in a 2-col cluster) crowded the
  // 4-col view and made the row labels collide.
  // Scott 2026-05-19 (later): floor at 8 so sectors always surface their
  // full 8-ticker roster regardless of column count. Below 4 cols this just
  // produces extra rows (3 rows × 3 cols = 9 → still capped at 8 by slice).
  const maxStocks=nCols*2;
  // Stack ticker over price/pct in narrow cells (3+ columns) so the
  // monospace price doesn't crash into the ticker symbol.
  const stackVertical=nCols>=3;
  const ac=accentColor||'#3b82f6';
  const expandedHere=expandedTicker&&validTickers.includes(expandedTicker)&&(!expandedSectorId||expandedSectorId===sector.id);
  const expandedStock=expandedHere?STOCKS[expandedTicker]:null;
  const expandedUp=expandedStock?_up(expandedStock.changePct):false;
  const shownCount=Math.min(validTickers.length,maxStocks);
  // Sector-level % change removed: sectors are curated clusters, not
  // tradable indices, so an average change reads as false precision.
  return (
    // Inline expansion: when a ticker is expanded, the cluster grows in
    // height to fit the chart + alerts + headlines. The parent grid uses
    // alignItems:'start', so only items BELOW this cluster in the same
    // column shift down — the OTHER column stays put. Earlier we tried an
    // absolute-positioned overlay to keep zero-reflow; user feedback
    // 2026-05-03 was that the cramped 1-cluster-wide overlay clipped the
    // chart, hid the ticker name, and read as "bleeding through" since
    // the bg matched the page bg without a clear container. Inline gives
    // the chart real room and lets us show a proper ticker header.
    <div className="cluster-card" style={{background:th.gridLine,border:`1px solid ${expandedHere?accentColor+'88':th.border}`,borderRadius:12,padding:'14px 16px',cursor:'pointer',transition:'all 0.18s',position:'relative'}}>
      {/* Header */}
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:8}}>
        <span style={{fontSize:f.title,fontWeight:700,color:th.text}}>{sector.name}</span>
        <span style={{fontSize:f.sub,fontWeight:600,color:th.sub,fontFamily:'DM Mono,monospace'}}>{shownCount} stocks</span>
      </div>
      {/* N-column stock grid. Expansion renders after the LAST cell in the
          same row as the clicked ticker so the sibling in the adjacent column
          stays in place above the graph (not pushed below it). */}
      <div style={{display:'grid',gridTemplateColumns:`repeat(${nCols},1fr)`,gap:'6px 12px'}}>
        {(()=>{const tickers=validTickers.slice(0,maxStocks);const expIdx=expandedHere?tickers.indexOf(expandedTicker):-1;const expRow=expIdx>=0?Math.floor(expIdx/nCols):-1;return tickers.map((t,i)=>{
          const sk=STOCKS[t];
          const su=_up(sk.changePct);
          // Match ticker AND (when caller provides sectorId context) the
          // current sector. Lets NVDA in Technology highlight independently
          // from NVDA in AI & Semis when D-pad picks one or the other.
          const isFocused=t===focusedTicker&&(!focusedSectorId||focusedSectorId===sector.id);
          const isExpanded=t===expandedTicker&&(!expandedSectorId||expandedSectorId===sector.id);
          const stale=_staleClass(t);
          const isLastInRow=(i%nCols===nCols-1)||(i===tickers.length-1);
          const renderExpansion=expRow>=0&&isLastInRow&&Math.floor(i/nCols)===expRow;
          return (
            <React.Fragment key={t}>
              <div
                ref={isFocused?(el=>{el&&el.scrollIntoView&&el.scrollIntoView({block:'nearest',behavior:'smooth'});})  :null}
                role="button" tabIndex={0} aria-label={`${t} ${sector.name}`}
                onClick={()=>onStockClick&&onStockClick(t,sector.id)}
                onKeyDown={e=>{if((e.key==='Enter'||e.key===' '||e.keyCode===13)&&!e.repeat){e.preventDefault();onStockClick&&onStockClick(t,sector.id);}}}
                style={{display:'flex',flexDirection:'column',justifyContent:'flex-start',alignItems:'stretch',padding:'7px 9px',borderRadius:7,background:isFocused||isExpanded?`${ac}33`:th.gridLine,border:`3px solid ${isFocused||isExpanded?ac:'transparent'}`,cursor:onStockClick?'pointer':'default',transition:'all 0.15s ease-out',boxShadow:isFocused||isExpanded?`0 0 0 3px ${ac}66, 0 0 18px ${ac}55`:'none',position:isFocused||isExpanded?'relative':'static',zIndex:isFocused||isExpanded?2:1,minWidth:0,gap:2}}>
                {nCols>=4 ? (
                  <>
                    {/* 4-col: ticker + % on top row, price on bottom.
                        Font sizes reduced (title not title+1) because at
                        ~65-77px cell width "GOOGL" + "-2.30%" was clipping. */}
                    <div style={{display:'flex',justifyContent:'space-between',alignItems:'baseline',minWidth:0,gap:3}}>
                      <div style={{fontSize:f.title,fontWeight:700,lineHeight:1.1,color:th.text,whiteSpace:'nowrap',overflow:'hidden',minWidth:0,flex:1}}>{t}</div>
                      <div className={stale} style={{fontSize:f.sub-2,color:su?'#16a34a':'#ef4444',fontFamily:'DM Mono,monospace',whiteSpace:'nowrap',flexShrink:0}}>{_pct(sk.changePct,1)}</div>
                    </div>
                    <div className={stale} style={{fontSize:f.title-2,fontWeight:600,fontFamily:'DM Mono,monospace',lineHeight:1.1,color:th.text,whiteSpace:'nowrap',overflow:'hidden'}}>{_money(sk.price, undefined, true)}</div>
                  </>
                ) : stackVertical ? (
                  <>
                    {/* 2–3 col: ticker on top, price + % on bottom row */}
                    <div style={{minWidth:0,whiteSpace:'nowrap',overflow:'hidden'}}>
                      <div style={{fontSize:f.title+1,fontWeight:700,lineHeight:1.1,color:th.text}}>{t}</div>
                    </div>
                    <div style={{display:'flex',gap:4,alignItems:'baseline',minWidth:0,overflow:'hidden'}}>
                      <div className={stale} style={{fontSize:f.title+1,fontWeight:600,fontFamily:'DM Mono,monospace',lineHeight:1.1,color:th.text,whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis',minWidth:0}}>{_money(sk.price)}</div>
                      <div className={stale} style={{fontSize:f.sub,color:su?'#16a34a':'#ef4444',fontFamily:'DM Mono,monospace',whiteSpace:'nowrap',flexShrink:0}}>{_pct(sk.changePct)}</div>
                    </div>
                  </>
                ) : (
                  <>
                    {/* 1-col / row mode: ticker left, price+% right */}
                    <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',minWidth:0}}>
                      <div style={{minWidth:0,flex:1,whiteSpace:'nowrap',overflow:'hidden'}}>
                        <div style={{fontSize:f.title+1,fontWeight:700,lineHeight:1.1,color:th.text}}>{t}</div>
                      </div>
                      <div style={{textAlign:'right',display:'flex',flexDirection:'column',gap:0,alignItems:'flex-end'}}>
                        <div className={stale} style={{fontSize:f.title+1,fontWeight:600,fontFamily:'DM Mono,monospace',lineHeight:1.1,color:th.text}}>{_money(sk.price)}</div>
                        <div className={stale} style={{fontSize:f.sub,color:su?'#16a34a':'#ef4444',fontFamily:'DM Mono,monospace',marginTop:3}}>{_pct(sk.changePct)}</div>
                      </div>
                    </div>
                  </>
                )}
              </div>
              {renderExpansion&&(
                <div className="expanded-stock-host" style={{gridColumn:`1 / -1`,background:th.bg,border:`2px solid ${accentColor}`,borderRadius:12,boxShadow:`0 6px 20px rgba(0,0,0,0.45), 0 0 0 1px ${accentColor}44`,marginTop:4,overflow:'hidden',zIndex:1,position:'relative'}}>
                  {/* Header */}
                  <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',padding:'10px 14px',borderBottom:`1px solid ${accentColor}33`,background:`linear-gradient(180deg, ${accentColor}18, transparent)`}}>
                    <div style={{display:'flex',alignItems:'baseline',gap:10,minWidth:0,flex:1}}>
                      <span style={{fontSize:f.title+6,fontWeight:800,color:th.text,letterSpacing:'0.02em'}}>{expandedTicker}</span>
                      <span style={{fontSize:f.sub,color:th.sub,whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis'}}>{expandedStock?.name||''}</span>
                    </div>
                    <div style={{display:'flex',alignItems:'baseline',gap:10}}>
                      <span className={_staleClass(expandedTicker)} style={{fontSize:f.title+3,fontWeight:700,color:th.text,fontFamily:'DM Mono,monospace'}}>{_money(expandedStock?.price)}</span>
                      <span className={_staleClass(expandedTicker)} style={{fontSize:f.title,fontWeight:700,color:expandedUp?'#16a34a':'#ef4444',fontFamily:'DM Mono,monospace'}}>{_pct(expandedStock?.changePct)}</span>
                    </div>
                  </div>
                  <ExpandedStock ticker={expandedTicker} range={expandedRange||'5D'} onRangeChange={onRangeChange} fontSize={fontSize} accentColor={ac} isPro={isPro}
                    compact={true}
                    alerts={(alerts||[]).filter(a=>a.symbol===expandedTicker)} onAddAlert={onAddAlert} onDeleteAlert={onDeleteAlert} onPaywall={onPaywall} theme={theme}/>
                </div>
              )}
            </React.Fragment>
          );
        })})()}
      </div>
    </div>
  );
}

// ── Gainers / Losers ───────────────────────────────────────────────────────
function GainersLosers({list, type, fontSize, onStockClick, focusedTicker, focusedSectorId, accentColor, expandedTicker, expandedRange, onRangeChange, isPro, alerts, onAddAlert, onDeleteAlert, onPaywall, theme}) {
  const ac=accentColor||'#3b82f6';
  const expandedRef=React.useRef(null);
  const sectorId=type==='gainers'?'__gainers':'__losers';
  const display=list.slice(0,16);
  const expandedHere=expandedTicker&&display.includes(expandedTicker);
  React.useEffect(()=>{
    if(expandedHere&&expandedRef.current){
      try{expandedRef.current.scrollIntoView({behavior:'smooth',block:'nearest'});}catch{}
    }
  },[expandedTicker,expandedHere]);
  const half=Math.ceil(display.length/2);
  const leftCol=display.slice(0,half);
  const rightCol=display.slice(half);
  const renderRow=(t)=>{
    if(!t)return <div style={{minHeight:54}}/>;
    const selected=(t===focusedTicker&&(!focusedSectorId||focusedSectorId===sectorId))||t===expandedTicker;
    return (
      <StockRow
        ticker={t}
        selected={selected}
        onClick={()=>onStockClick&&onStockClick(t)}
        fontSize={fontSize}
        accentColor={ac}
        alerts={alerts||[]}
        theme={theme}
        compact={true}
      />
    );
  };
  const rowCount=Math.max(leftCol.length,rightCol.length);
  return (
    <div style={{display:'flex',flexDirection:'column',gap:'4px'}}>
      {Array.from({length:rowCount},(_,i)=>{
        const lt=leftCol[i];
        const rt=rightCol[i];
        const rowExpanded=expandedHere&&(lt===expandedTicker||rt===expandedTicker);
        return (
          <React.Fragment key={i}>
            <div style={{display:'grid',gridTemplateColumns:'minmax(0,1fr) minmax(0,1fr)',columnGap:8,minWidth:0,alignItems:'start'}}>
              {renderRow(lt)}
              {renderRow(rt)}
            </div>
            {rowExpanded&&(
              <div ref={expandedRef} className="expanded-stock-host" style={{minWidth:0,zIndex:1,position:'relative'}}>
                  <ExpandedStock ticker={expandedTicker} range={expandedRange||'5D'} onRangeChange={onRangeChange} fontSize={fontSize} accentColor={ac} isPro={isPro}
                  compact={true} theme={theme}
                  alerts={(alerts||[]).filter(a=>a.symbol===expandedTicker)} onAddAlert={onAddAlert} onDeleteAlert={onDeleteAlert} onPaywall={onPaywall}/>
              </div>
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
}

// ── Ticker Bar ─────────────────────────────────────────────────────────────
//
// `indices` (optional) — array of major US-market indices the receiver passes
// in from index.html. Previously these rendered in a dedicated top bar
// (IndicesBar); we now interleave them into the rolling tape so the dashboard
// reclaims that vertical space and the indices get visible at a reading
// cadence rather than a static row.
//
// Layout pattern: 1 index inserted after every 4 stocks. With 8 indices and
// the watchlist looping, each index surfaces ~once every 25 seconds at the
// default scroll speed — frequent enough to act as ambient market context,
// not so frequent that they crowd the watchlist.
//
// Color-coded badges (2026-05-01 test): each ticker gets a small filled
// circle in its sector color before the symbol — Tech is brand-blue, Energy
// amber, Healthcare green, etc. (See ALL_SECTORS for the full mapping.)
// Indices use the same amber (#f59e0b) used for the index value so the row
// reads as one cohesive unit.
//
// Distinct color: indices render in amber (#f59e0b) to read as "different
// kind of thing" vs the white-on-monochrome stock entries; the up/down %
// pill keeps the green/red convention so glanceability is preserved.
//
// Font bumped sm:17/md:20/lg:25 → sm:20/md:24/lg:30 per user feedback that
// the bottom tape was hard to read from couch distance.

// Build a quick ticker → sector-color map from ALL_SECTORS. A ticker can
// belong to multiple sectors (e.g. NVDA is in 'tech' and 'ai'); first match
// wins, which mirrors how STOCKS[ticker].sector is set elsewhere.
const TICKER_COLOR = (() => {
  const m = {};
  if (typeof ALL_SECTORS !== 'undefined') {
    for (const sec of ALL_SECTORS) {
      for (const t of (sec.tickers || [])) {
        if (!m[t]) m[t] = sec.color;
      }
    }
  }
  return m;
})();
function colorForTicker(sym){
  if (TICKER_COLOR[sym]) return TICKER_COLOR[sym];
  // Fallback: use the stock's stored .sector (set in STOCKS[sym]) → its color.
  const stk = (typeof STOCKS !== 'undefined') ? STOCKS[sym] : null;
  if (stk && stk.sector && typeof ALL_SECTORS !== 'undefined') {
    const s = ALL_SECTORS.find(x => x.id === stk.sector);
    if (s) return s.color;
  }
  return '#94a3b8';  // neutral slate
}

// Ticker → corporate-domain map for Clearbit logo lookup. We hand-curate
// rather than guess so we don't show wrong-company logos (a blind
// AAPL → aapl.com would hit a parked-domain). New tickers fall back to
// the letter-mark chip until added here.
const TICKER_DOMAIN = {
  AAPL:'apple.com', MSFT:'microsoft.com', NVDA:'nvidia.com', GOOGL:'abc.xyz',
  META:'meta.com', NFLX:'netflix.com', AMD:'amd.com',
  AMZN:'amazon.com', WMT:'walmart.com', COST:'costco.com', HD:'homedepot.com',
  TSLA:'tesla.com', GM:'gm.com', RIVN:'rivian.com', F:'ford.com',
  JNJ:'jnj.com', PFE:'pfizer.com', UNH:'unitedhealthgroup.com',
  ABBV:'abbvie.com', MRK:'merck.com', LLY:'lilly.com',
  XOM:'exxonmobil.com', CVX:'chevron.com', COP:'conocophillips.com', SLB:'slb.com',
  JPM:'jpmorganchase.com', BAC:'bankofamerica.com', GS:'goldmansachs.com',
  WFC:'wellsfargo.com',
  AMT:'americantower.com', PLD:'prologis.com', SPG:'simon.com',
  WELL:'welltower.com', EQR:'equityapartments.com',
  DAL:'delta.com', UAL:'united.com', MAR:'marriott.com',
  ABNB:'airbnb.com', RCL:'royalcaribbean.com',
  MCD:'mcdonalds.com', SBUX:'starbucks.com', KO:'coca-colacompany.com',
  PEP:'pepsico.com', YUM:'yum.com',
  CAT:'caterpillar.com', DE:'deere.com', HON:'honeywell.com', GE:'ge.com', UPS:'ups.com',
  MRNA:'modernatx.com', BNTX:'biontech.com', GILD:'gilead.com',
  REGN:'regeneron.com', VRTX:'vrtx.com',
  LMT:'lockheedmartin.com', RTX:'rtx.com', NOC:'northropgrumman.com',
  GD:'gd.com', BA:'boeing.com',
  T:'att.com', VZ:'verizon.com', TMUS:'t-mobile.com', CHTR:'corporate.charter.com',
  LIN:'linde.com', APD:'airproducts.com', NEM:'newmont.com', FCX:'fcx.com',
  AVGO:'broadcom.com', TSM:'tsmc.com', MU:'micron.com', SMCI:'supermicro.com',
  COIN:'coinbase.com', MSTR:'microstrategy.com', MARA:'mara.com',
  RIOT:'riotplatforms.com', HOOD:'robinhood.com',
  DIS:'disney.com', WBD:'wbd.com', SPOT:'spotify.com', ROKU:'roku.com',
  BABA:'alibabagroup.com', TM:'global.toyota', NVS:'novartis.com',
  SHOP:'shopify.com', ASML:'asml.com',
};

// Logo chip: real brand image via Clearbit when we have a domain mapped.
// Per 2026-05-01 product call: when no logo is available (unmapped ticker
// OR Clearbit 404s), we render NOTHING — the row just shows the symbol +
// price + change without a chip. Keeps the rolling tape uncluttered for
// long-tail tickers and avoids guessing a wrong-company image.
//
// Indices (DJI / SPX / etc.) keep their amber acronym chip because there
// IS no real "logo" for an index, and the colored chip is what
// distinguishes them from stocks in the rolling tape.
//
// Module-level negative cache for failed logo domains. Without it, every
// time the rolling tape's items array shifts (e.g. watchlist edit, indices
// SSE update) the React reconciler can re-mount TickerLogo at a different
// position; the local `errored` state resets to false; the <img> issues a
// fresh request. Over a 24h session with frequent SSE-driven re-renders
// that's thousands of repeated favicon requests per logo Google can't serve.
// 2026-05-07: switched from cross-origin Google favicons (every request
// status-0 / CORS-blocked, ~85 failures per scroll cycle) to a locally
// bundled set of brand logos at /assets/logos/<TICKER>.png. The bundle
// scripts (bundle-receiver-for-lg.sh, bundle-receiver-for-tizen.sh)
// already copy /assets/* into the offline TV bundles, so this works
// from file://-loaded receivers too. Tickers without a usable logo
// (small/missing source from the bulk fetch) fall back to a letter-mark
// chip painted in the ticker's sector color.
//
// BUNDLED_LOGO_SYMS is the explicit allowlist of tickers we ship a real
// PNG for. Anything outside this set short-circuits to the letter-mark
// chip with zero network requests. Without this, every unbundled ticker
// fires a 404 that pollutes the console (74 errors observed in QA after
// the first cut). When you add a new logo to web/assets/logos/, also
// add the symbol here.
const BUNDLED_LOGO_SYMS = new Set([
  'AAPL','ABBV','ABNB','ADA','AMD','AMGN','AMT','AMZN','APD','ARM',
  'ASML','ASTS','AVB','AVGO','AXP','BA','BABA','BAC','BIDU','BIIB',
  'BIRK','BLK','BMY','BNTX','BTC','C','CART','CAT','CCL','CHTR',
  'CLSK','CMCSA','COIN','COP','COST','CRM','CVS','CVX','DAL','DD',
  'DE','DIS','DISH','DOGE','DOW','DPZ','EBAY','ECL','EOG','EQR',
  'ETH','F','FCX','FUBO','GD','GE','GILD','GIS','GM','GOOGL',
  'GS','HD','HII','HLT','HMC','HON','HOOD','HSY','INTC','ITW',
  'JNJ','JPM','KO','KVYO','LB','LCID','LDOS','LHX','LIN','LINK',
  'LLY','LMT','LOW','LTC','LUMN','LUV','MAR','MARA','MCD','META',
  'MMM','MPC','MRK','MRNA','MS','MSFT','MSTR','MU','NEM','NFLX',
  'NKE','NOC','NUE','NVDA','NVS','O','ORCL','OXY','PARA','PEP',
  'PFE','PLD','PSX','PYPL','QCOM','RCL','RDDT','REGN','RIOT','RIVN',
  'ROKU','RTX','SAP','SBUX','SHOP','SIRI','SLB','SMCI','SMR','SOL',
  'SONY','SPG','SPOT','SQ','STLA','T','TDG','TEM','TGT','TM',
  'TMO','TMUS','TSLA','TSM','UAL','UNH','UPS','VRTX','VZ','WBD',
  'WELL','WFC','WMT','XOM','XRP','YUM',
]);
const _missingLogo = new Set();
function TickerLogo({ sym, color, height, isIndex }) {
  const [errored, setErrored] = useState(false);
  const radius = Math.round(height * 0.22);
  const chipFs = Math.round(height * 0.55);
  if (isIndex) {
    return (
      <span style={{
        display:'inline-flex',alignItems:'center',justifyContent:'center',
        height,minWidth:height+6,padding:`0 ${Math.round(chipFs*0.45)}px`,
        borderRadius:radius,background:'#f59e0b',color:'#0f1728',
        fontFamily:'Space Grotesk,sans-serif',fontWeight:800,fontSize:chipFs,
        letterSpacing:'0.06em',flexShrink:0,boxShadow:'0 0 8px rgba(245,158,11,0.35)',
      }}>{sym}</span>
    );
  }
  const bg = color || '#94a3b8';
  // Letter-mark fallback chip — also the render path when local logo file
  // is missing (404 from /assets/logos/<SYM>.png).
  const letterMark = (
    <span style={{
      display:'inline-flex',alignItems:'center',justifyContent:'center',
      height,minWidth:height+6,padding:`0 ${Math.round(chipFs*0.45)}px`,
      borderRadius:radius,background:bg,color:'#0f1728',
      fontFamily:'Space Grotesk,sans-serif',fontWeight:800,fontSize:chipFs,
      letterSpacing:'0.06em',flexShrink:0,boxShadow:`0 0 8px ${bg}55`,
    }}>{sym}</span>
  );
  if (!BUNDLED_LOGO_SYMS.has(sym) || errored || _missingLogo.has(sym)) return letterMark;
  return (
    <span style={{
      display:'inline-flex',alignItems:'center',justifyContent:'center',
      height,width:height,borderRadius:radius,
      background:'#fff',padding:Math.round(height*0.07),
      flexShrink:0,boxShadow:`0 0 8px ${bg}33`,
      overflow:'hidden',
    }}>
      <img
        src={(typeof window!=='undefined'&&window.location&&window.location.protocol==='file:')?`assets/logos/${sym}.png`:`/assets/logos/${sym}.png`}
        onError={() => { _missingLogo.add(sym); setErrored(true); }}
        alt=""
        loading="lazy"
        decoding="async"
        style={{height:'100%',width:'100%',objectFit:'contain',display:'block'}}
      />
    </span>
  );
}

function TickerBar({fontSize, speed, isPro, onUpgrade, indices, theme}) {
  const th=_th(theme);
  const fs={sm:20,md:24,lg:30}[fontSize] || 24;
  // Exclude commodity futures (= in symbol), exchange-qualified search results
  // (: in symbol like SB:NYSE), commodities sector, and IPOs from the tape.
  // Include all eligible stocks regardless of whether a price has arrived yet —
  // filtering by `typeof s.price==='number'` caused the list to grow as SSE
  // prices streamed in, changing total ticker-track width mid-scroll and making
  // translateX(-33.333%) jump to a different pixel offset each time.
  // Stocks without prices show the stale placeholder (—) until data arrives.
  const tickers=React.useMemo(()=>Object.keys(STOCKS).filter(sym=>{
    if(!sym||sym.includes(':')||sym.includes('='))return false;
    const s=STOCKS[sym];
    return !(!s||s.sector==='commodities'||s.sector==='ipos');
  }),[]);

  // Interleave: 1 index after every N stocks. N=4 spreads the 8 indices
  // pretty evenly across the loop without crowding.
  const INDEX_EVERY = 4;
  const interleaved=[];
  const idxList=Array.isArray(indices)?indices:[];
  for(let i=0;i<tickers.length;i++){
    interleaved.push({kind:'stock',sym:tickers[i]});
    if(idxList.length && i%INDEX_EVERY===INDEX_EVERY-1){
      // Walk through the indices in order, looping back when we run out.
      const idx=idxList[Math.floor(i/INDEX_EVERY)%idxList.length];
      interleaved.push({kind:'index',data:idx});
    }
  }
  // Triple-loop for the seamless ticker scroll.
  const items=[...interleaved,...interleaved,...interleaved];

  // Container grows with font: lg gets a taller bar so the percent badge
  // doesn't crash into the borders.
  const barH={sm:60,md:68,lg:80}[fontSize] || 68;
  const showTickerUpgrade=!isPro&&onUpgrade;
  const upgradeSlotW={sm:210,md:236,lg:276}[fontSize] || 236;

  return (
    <div style={{position:'absolute',bottom:0,left:0,right:0,height:barH,background:th.overlay,borderTop:`1px solid ${th.overlayBorder}`,overflow:'hidden',display:'flex',alignItems:'center'}}>
      <div style={{position:'absolute',left:0,top:0,bottom:0,right:showTickerUpgrade?upgradeSlotW:0,overflow:'hidden',display:'flex',alignItems:'center',minWidth:0}}>
        <div className="ticker-track" style={{'--ticker-speed':`${speed||80}s`}}>
          {items.map((it,i)=>{
            // Logo + ticker line. TickerLogo handles three cases internally:
            // (a) index → amber acronym chip
            // (b) stock with mapped Clearbit domain → real brand logo
            // (c) stock without a logo → returns null (just blank space)
            const chipH = Math.round(fs * 1.15);
            const INDEX_ACRO = { 'DOW':'DJI', 'S&P 500':'SPX', 'NASDAQ':'IXIC', 'Russell':'RUT' };
            // Reserve a min-width per entry so the slot doesn't grow when
            // price+pct hydrate from "—" placeholders to real values. Without
            // this, every ticker entry shifts ~50% wider as data arrives,
            // producing visible CLS on the auth-gate render path. Width
            // tuned for the longest realistic line ("$1234.56 ▲12.34%") at
            // each font scale. (2026-05-06)
            const ENTRY_MIN_W = { sm: 200, md: 230, lg: 280 }[fontSize] || 230;
            if(it.kind==='index'){
              const d=it.data||{};
              const valueOk=Number.isFinite(Number(d.value));
              const pctOk=Number.isFinite(Number(d.changePct));
              const up=!pctOk||Number(d.changePct)>=0;
              // Amber palette — dark-only since 2026-05-18.
              const idxNameC = '#f59e0b';
              const idxValC  = '#fbbf24';
              const idxDotC  = 'rgba(245,158,11,0.25)';
              return (
                <span key={i} style={{display:'inline-flex',alignItems:'center',gap:10,marginRight:32,whiteSpace:'nowrap',fontSize:fs,fontFamily:'DM Mono,monospace',minWidth:ENTRY_MIN_W,flexShrink:0}}>
                  <span style={{fontWeight:800,color:idxNameC,fontFamily:'Space Grotesk,sans-serif',letterSpacing:'0.04em'}}>{d.name}</span>
                  <span style={{color:idxValC}}>{valueOk?Number(d.value).toLocaleString('en-US',{minimumFractionDigits:2,maximumFractionDigits:2}):'—'}</span>
                  <span style={{color:up?'#16a34a':'#ef4444',display:'inline-flex',alignItems:'center',gap:2}}>
                    <span style={{fontSize:fs,lineHeight:1,display:'inline-block'}}>{up?'▲':'▼'}</span>
                    {pctOk?`${Math.abs(Number(d.changePct)).toFixed(2)}%`:'—'}
                  </span>
                  <span style={{color:idxDotC,marginLeft:4}}>·</span>
                </span>
              );
            }
            const s=STOCKS[it.sym]; if(!s) return null;
            const up=_up(s.changePct);
            const chipBg = colorForTicker(it.sym);
            const stale=_staleClass(it.sym);
            const hasLogo = BUNDLED_LOGO_SYMS.has(it.sym);
            return (
              <span key={i} style={{display:'inline-flex',alignItems:'center',gap:10,marginRight:32,whiteSpace:'nowrap',fontSize:fs,fontFamily:'DM Mono,monospace',minWidth:ENTRY_MIN_W,flexShrink:0}}>
                {hasLogo&&<TickerLogo sym={it.sym} color={chipBg} height={chipH}/>}
                <span style={{fontWeight:700,color:th.text,fontFamily:'Space Grotesk,sans-serif'}}>{it.sym}</span>
                <span className={stale} style={{color:th.sub}}>{_money(s.price)}</span>
                <span className={stale} style={{color:up?'#16a34a':'#ef4444',display:'inline-flex',alignItems:'center',gap:2}}>
                  <span style={{fontSize:fs,lineHeight:1,display:'inline-block'}}>{typeof s.changePct==='number'?(up?'▲':'▼'):''}</span>
                  {_absPct(s.changePct)}
                </span>
                <span style={{color:th.border,marginLeft:4}}>·</span>
              </span>
            );
          })}
        </div>
      </div>
      {showTickerUpgrade&&(
        // Paywall CTA pinned to the right edge. Treated as a button (rounded
        // chip with border + background) so it doesn't read as a stock entry
        // sliding into the ticker rotation — user 2026-05-03 saw "Speed
        // control · Pro+" appear next to XOM/JNJ/etc. and reported it as a
        // misplaced ticker. The wider gradient fade ensures the leftmost
        // ticker letters are dimmed before the pill so the boundary is
        // unambiguous.
        <button type="button" onClick={onUpgrade} data-tv-action="ticker-speed-upgrade" style={{position:'absolute',right:0,top:0,bottom:0,width:upgradeSlotW,background:th.overlay,display:'flex',alignItems:'center',justifyContent:'flex-end',paddingRight:12,paddingLeft:12,cursor:'pointer',gap:6,zIndex:2,borderLeft:`1px solid ${th.overlayBorder}`,borderTop:'none',borderRight:'none',borderBottom:'none',color:'inherit',fontFamily:'Space Grotesk,sans-serif'}}>
          <span style={{display:'inline-flex',alignItems:'center',gap:6,padding:'4px 10px',borderRadius:6,background:'rgba(245,158,11,0.10)',border:'1px solid rgba(245,158,11,0.45)'}}>
            <span style={{fontSize:fs-6,color:'#f59e0b'}}>🔒</span>
            {/* Label says "Pro+" because the gate upstream is `tier === 'proplus'` —
                Pro tier accounts still see the lock. Showing "Pro" was misleading
                ("locked Pro feature on a Pro account?") — reported 2026-05-02. */}
            <span style={{fontSize:fs-5,fontWeight:700,color:'#f59e0b',whiteSpace:'nowrap',letterSpacing:'0.02em'}}>Speed control · Pro+</span>
          </span>
        </button>
      )}
    </div>
  );
}

// Export everything to window
// Note: INDICES/COMMODITIES/IndicesBar live in the inline script of receiver/index.html,
// not here — don't re-export them.
Object.assign(window, {
  STOCKS, ALL_SECTORS, GAINERS, LOSERS, RANGES, FREE_RANGES, FREE_WATCHLIST_MAX, FS,
  seededRand, genPrices, toPath, toArea,
  MiniChart, FullChart, StockRow, ExpandedStock, SectorCluster, GainersLosers, TickerBar,
  useDwellPager,
});
